/**
 * Greek (EL) — secondary locale for VistoChat dashboard.
 *
 * Mirrors en.ts exactly. All key paths must match. When adding a new key
 * in en.ts, add the same key here with the Greek translation.
 *
 * Style guide:
 *   - Address user as singular informal ("εσύ" not "εσείς") to match the
 *     existing dashboard tone established before 2026-05-03.
 *   - Keep technical terms in English when they're industry-standard
 *     (e.g. "embed code", "RAG", "API token", "webhook") rather than
 *     forcing awkward Greek calques.
 *   - For UI verbs prefer imperative singular ("Αποθήκευσε" not "Αποθήκευση")
 *     when the action is direct, and noun form ("Αποθήκευση") when it's a
 *     status / heading.
 */
export default {
  common: {
    save: 'Αποθήκευση',
    saving: 'Αποθήκευση…',
    saved: 'Αποθηκεύτηκε',
    cancel: 'Ακύρωση',
    confirm: 'Επιβεβαίωση',
    delete: 'Διαγραφή',
    edit: 'Επεξεργασία',
    create: 'Δημιουργία',
    close: 'Κλείσιμο',
    back: 'Πίσω',
    next: 'Επόμενο',
    previous: 'Προηγούμενο',
    loading: 'Φόρτωση…',
    refresh: 'Ανανέωση',
    copy: 'Αντιγραφή',
    copied: 'Αντιγράφηκε',
    undo: 'Αναίρεση',
    yes: 'Ναι',
    no: 'Όχι',
    optional: 'προαιρετικό',
    required: 'υποχρεωτικό',
    search: 'Αναζήτηση',
    searchPlaceholder: 'Αναζήτηση…',
    none: 'Κανένα',
    all: 'Όλα',
    selectOption: 'Διάλεξε επιλογή',
    notAvailable: '—',
    learnMore: 'Μάθε περισσότερα',
    viewAll: 'Δες όλα',
    viewMore: 'Δες περισσότερα',
    viewLess: 'Δες λιγότερα',
    showAdvanced: 'Εμφάνιση advanced',
    hideAdvanced: 'Απόκρυψη advanced',
    apply: 'Εφαρμογή',
    submit: 'Υποβολή',
    retry: 'Δοκίμασε ξανά',
    download: 'Λήψη',
    upload: 'Ανέβασμα',
    enabled: 'Ενεργό',
    disabled: 'Ανενεργό',
    active: 'Ενεργό',
    inactive: 'Ανενεργό',
    draft: 'Πρόχειρο',
    paused: 'Σε παύση',
    archived: 'Αρχειοθετημένο',
    online: 'Online',
    offline: 'Offline',
    connect: 'Σύνδεση',
    disconnect: 'Αποσύνδεση',
    connected: 'Συνδεδεμένο',
    notConnected: 'Μη συνδεδεμένο',
    error: 'Σφάλμα',
    warning: 'Προσοχή',
    info: 'Πληροφορία',
    success: 'Επιτυχία',
    today: 'Σήμερα',
    yesterday: 'Χθες',
    last7Days: 'Τελευταίες 7 ημέρες',
    last30Days: 'Τελευταίες 30 ημέρες',
    last90Days: 'Τελευταίες 90 ημέρες',
    last365Days: 'Τελευταίες 365 ημέρες',
    monthly: 'Μηνιαία',
    annual: 'Ετήσια',
    yearly: 'Ετήσια',
    perMonth: '/μήνα',
    perYear: '/έτος',
  },
  nav: {
    companies: 'Εταιρείες',
    dashboard: 'Dashboard',
    bots: 'Bots',
    brains: 'Brains',
    conversations: 'Συνομιλίες',
    leads: 'Leads',
    knowledge: 'Γνώση (RAG)',
    plugins: 'Plugins',
    stats: 'Στατιστικά',
    team: 'Ομάδα',
    billing: 'Συνδρομή',
    settings: 'Ρυθμίσεις',
    account: 'Λογαριασμός',
    admin: 'Admin',
    adminStats: 'System stats',
    adminManaged: 'Managed subs',
    adminCompanies: 'Εταιρείες',
    adminFeedback: 'Σχόλια πελατών',
    adminUsers: 'Χρήστες',
    adminUsage: 'Χρήση & Κόστη',
    adminContactRequests: 'Contact requests',
    notifications: 'Ειδοποιήσεις',
    pushEnable: 'Ενεργοποίηση push ειδοποιήσεων (νέα leads + αιτήματα για άνθρωπο)',
    pushDisable: 'Push ειδοποιήσεις ΕΝΕΡΓΕΣ — κλικ για απενεργοποίηση',
    logout: 'Αποσύνδεση',
    profile: 'Προφίλ',
    language: 'Γλώσσα',
    languageEn: 'Αγγλικά',
    languageEl: 'Ελληνικά',
    languageSwitch: 'Αλλαγή γλώσσας',
    helpCenter: 'Βοήθεια',
    statusPage: 'Status',
    legalDocs: 'Νομικά',
    skipToContent: 'Μετάβαση στο περιεχόμενο',
    openMenu: 'Άνοιγμα μενού',
    closeMenu: 'Κλείσιμο μενού',
    primaryNav: 'Κύρια πλοήγηση',
    accountAria: 'Λογαριασμός',
    partnerBadge: 'Partner',
    partnerBadgeTitle: '{n} paid bots — Partner Program',
    sandboxLabel: 'Sandbox · Bun + Hono',
    upgrade: 'Αναβάθμιση',
    groups: {
      partner: 'Δίκτυο συνεργατών',
      overview: 'Επισκόπηση',
      manage: 'Διαχείριση',
      admin: 'Admin (Vistoweb)',
      account: 'Λογαριασμός',
    },
  },
  banner: {
    verifyEmailTitle: 'Επιβεβαίωσε το email σου',
    verifyEmailBody: 'Έλεγξε το inbox σου για τον σύνδεσμο που στείλαμε στο {email}',
    resend: 'Στείλε ξανά',
    resending: 'Αποστολή…',
    resendSuccess: 'Στάλθηκε email επιβεβαίωσης. Έλεγξε τα εισερχόμενά σου.',
    resendSentLabel: 'Στάλθηκε ✓',
    resendFailure: 'Δεν ήταν δυνατή η αποστολή. Δοκίμασε ξανά σε λίγο.',
  },
  footer: {
    company: 'Vistoweb Ε.Ε.',
    gemi: 'ΓΕΜΗ',
    afm: 'ΑΦΜ',
    address: 'Διεύθυνση',
    legal: 'Νομικά',
    terms: 'Όροι',
    privacy: 'Απόρρητο',
    cookies: 'Cookies',
    refund: 'Επιστροφές',
    aup: 'Αποδεκτή χρήση',
    dpa: 'DPA',
    subprocessors: 'Υποπάροχοι',
    sla: 'SLA',
  },
  auth: {
    login: {
      title: 'Καλώς ήρθες',
      subtitle: 'Συνδέσου στον λογαριασμό σου VistoChat',
      tagline: 'Το AI chatbot για φιλόδοξα websites.',
      email: 'Email',
      password: 'Κωδικός',
      passwordRequired: 'Συμπλήρωσε τον κωδικό σου.',
      remember: 'Να με θυμάσαι σε αυτή τη συσκευή',
      submit: 'Σύνδεση',
      submitting: 'Σύνδεση…',
      forgotPassword: 'Ξέχασες τον κωδικό σου;',
      noAccount: 'Δεν έχεις λογαριασμό;',
      signupLink: 'Δημιουργία λογαριασμού',
      or: 'ή',
      ssoGoogle: 'Συνέχεια με Google',
      ssoMicrosoft: 'Συνέχεια με Microsoft',
      ssoTermsNoticePrefix: 'Συνεχίζοντας με Google ή Microsoft αποδέχεσαι τους',
      ssoTermsNoticeAnd: 'και την',
      errors: {
        invalid: 'Λάθος email ή κωδικός.',
        generic: 'Η σύνδεση απέτυχε. Δοκίμασε ξανά.',
        ssoStateMismatch: 'Σφάλμα ασφάλειας SSO. Δοκίμασε ξανά.',
        ssoMissingCode: 'Η σύνδεση SSO ακυρώθηκε.',
        ssoMisconfigured: 'Το SSO δεν είναι ρυθμισμένο για αυτόν τον provider.',
        ssoNoVerifiedTeam: 'Το email σου δεν αντιστοιχεί σε εγκεκριμένο domain.',
      },
    },
    signup: {
      title: 'Δημιουργία λογαριασμού',
      subtitle: '{days} ημέρες δωρεάν δοκιμή του {plan}. Δεν χρειάζεται κάρτα.',
      subtitleInvite: 'Δημιούργησε λογαριασμό για να μπεις στην ομάδα που σε προσκάλεσε.',
      tagline: 'Το AI chatbot για φιλόδοξα websites.',
      fullName: 'Ονοματεπώνυμο',
      fullNamePlaceholder: 'π.χ. Δημήτρης Παπαδόπουλος',
      fullNameRequired: 'Πες μας πώς σε λένε.',
      teamName: 'Επωνυμία εταιρείας',
      teamNamePlaceholder: 'π.χ. Vistoweb',
      teamNameHelp: 'προαιρετικό',
      email: 'Email',
      password: 'Κωδικός',
      passwordHelp: 'τουλάχιστον 8 χαρακτήρες',
      passwordRequired: 'Διάλεξε έναν κωδικό.',
      passwordTooShort: 'Ο κωδικός πρέπει να είναι τουλάχιστον 8 χαρακτήρες.',
      passwordShow: 'Εμφάνιση',
      passwordHide: 'Απόκρυψη',
      acceptTermsPrefix: 'Συμφωνώ με τους',
      acceptTermsTerms: 'όρους χρήσης',
      acceptTermsAnd: 'και την',
      acceptTermsPrivacy: 'πολιτική απορρήτου',
      submit: 'Δημιουργία λογαριασμού',
      submitting: 'Δημιουργία…',
      hasAccount: 'Έχεις ήδη λογαριασμό;',
      loginLink: 'Σύνδεση',
      orSignupWith: 'ή εγγραφή με',
      errors: {
        emailExists: 'Αυτό το email χρησιμοποιείται ήδη.',
        validation: 'Έλεγξε τα πεδία της φόρμας και ξαναδοκίμασε.',
        passwordShort: 'Ο κωδικός πρέπει να έχει τουλάχιστον 8 χαρακτήρες.',
        nameShort: 'Συμπλήρωσε το ονοματεπώνυμό σου.',
        termsRequired: 'Πρέπει να αποδεχτείς τους όρους χρήσης.',
        rateLimit: 'Πολλές προσπάθειες εγγραφής. Δοκίμασε ξανά αργότερα.',
        disposableEmail: 'Δεν επιτρέπονται προσωρινά / disposable emails. Χρησιμοποίησε το πραγματικό σου email.',
        generic: 'Η εγγραφή απέτυχε. Δοκίμασε ξανά.',
      },
    },
    verify: {
      successTitle: 'Επιβεβαιώθηκε!',
      successBody: 'Το email σου είναι πλέον επιβεβαιωμένο. Πλήρης πρόσβαση σε όλες τις λειτουργίες.',
      successCta: 'Συνέχεια στο Dashboard',
      expiredTitle: 'Ο σύνδεσμος έληξε',
      expiredBody: 'Οι σύνδεσμοι επιβεβαίωσης ισχύουν 24 ώρες. Στείλε νέο email επιβεβαίωσης.',
      invalidTitle: 'Μη έγκυρος σύνδεσμος',
      invalidBody: 'Ο σύνδεσμος επιβεβαίωσης δεν είναι έγκυρος ή έχει ήδη χρησιμοποιηθεί.',
      pendingTitle: 'Επιβεβαίωση email',
      pendingBody: 'Χρησιμοποίησε τον σύνδεσμο που έλαβες στο email σου για να επιβεβαιώσεις τον λογαριασμό.',
      resend: 'Στείλε νέο σύνδεσμο',
      resending: 'Αποστολή…',
      resendSuccess: 'Στείλαμε νέο email — τσέκαρε το inbox σου.',
      resendFailure: 'Δεν ήταν δυνατή η αποστολή.',
      resendMustLogin: 'Πρέπει να συνδεθείς πρώτα για να ζητήσεις νέο σύνδεσμο.',
      backToDashboard: 'Πίσω στο dashboard',
    },
    invite: {
      title: 'Πρόσκληση σε ομάδα',
      loadingPreview: 'Φόρτωση…',
      tokenMissing: 'Λείπει το token.',
      loadError: 'Σφάλμα φόρτωσης πρόσκλησης.',
      invalidTitle: 'Μη έγκυρη πρόσκληση',
      statusExpired: 'Αυτή η πρόσκληση έχει λήξει.',
      statusAccepted: 'Αυτή η πρόσκληση έχει ήδη χρησιμοποιηθεί.',
      statusInvalid: 'Άκυρη πρόσκληση.',
      backToVistoChat: '← Επιστροφή στο VistoChat',
      invitedAs: 'Έχεις προσκληθεί να γίνεις {role} στην ομάδα {team}.',
      invitedFor: 'Πρόσκληση για {email}',
      invitedBy: 'από {inviter}',
      expiresOn: 'Λήγει {date}',
      acceptInstruction: 'Συνδέσου με το email {email} για να αποδεχτείς — ή δημιούργησε νέο λογαριασμό.',
      goToLogin: 'Σύνδεση',
      createAccountCta: 'Δημιουργία λογαριασμού',
      acceptCta: 'Αποδέξου την πρόσκληση',
      accepting: 'Αποδοχή…',
      acceptSuccess: 'Καλωσήρθες στην ομάδα!',
      alreadyMember: 'Είσαι ήδη μέλος αυτής της ομάδας — η πρόσκληση μαρκαρίστηκε ως αποδεκτή.',
      acceptFailure: 'Δεν ήταν δυνατή η αποδοχή. Δοκίμασε ξανά.',
      emailMismatch: 'Συνδέσου με {email} για να αποδεχτείς αυτή την πρόσκληση.',
      differentAccountWarn: 'Είσαι συνδεδεμένος ως {current}. Αυτή η πρόσκληση είναι για {target}.',
      signInDifferent: 'Σύνδεση με άλλο λογαριασμό',
    },
    ssoDone: {
      finishing: 'Ολοκλήρωση σύνδεσης…',
    },
    demo: {
      loading: 'Φόρτωση demo…',
      loadingSub: 'Σε συνδέουμε σε δείγμα workspace για να εξερευνήσεις το dashboard.',
      failed: 'Αδυναμία φόρτωσης demo',
      goLogin: 'Επιστροφή στη σύνδεση',
    },
  },
  dashboard: {
    welcome: 'Καλώς ήρθες στο VistoChat 👋',
    subtitle: 'Πανοραμική εικόνα των bots, συνομιλιών και leads σου.',
    newBotCta: '+ Νέο bot',
    statsAriaLabel: 'Κύριες μετρικές',
    stats: {
      activeBots: 'Ενεργά bots',
      conversations30d: 'Συνομιλίες (30d)',
      leads30d: 'Leads (30d)',
      conversion: 'Conversion (30d)',
      conversionMeta: 'leads / chats',
      deltaVs: 'vs προηγ. 30d',
    },
    sections: {
      recentLeads: 'Πρόσφατα leads',
      recentChats: 'Πρόσφατες συνομιλίες',
      allLeads: 'Όλα →',
      allChats: 'Όλες →',
    },
    empty: {
      noLeads: 'Δεν υπάρχουν leads ακόμα.',
      noChats: 'Δεν υπάρχουν συνομιλίες ακόμα.',
      seeEmbed: 'Δες το embed code →',
      addFirstBot: 'Πρόσθεσε το πρώτο σου bot →',
    },
    list: {
      leadFallback: 'Lead χωρίς όνομα',
      messagesCount: '{n} μηνύματα',
      leadPill: 'lead',
    },
  },
  notFound: {
    title: 'Δεν βρέθηκε η σελίδα',
    body: 'Η σελίδα που ψάχνεις δεν υπάρχει ή έχει μετακινηθεί.',
    backHome: 'Πίσω στο Dashboard',
  },
  demo: {
    banner: {
      text: 'Εξερευνείς ένα read-only demo workspace. Δημιούργησε δωρεάν λογαριασμό για να κρατήσεις τις αλλαγές σου.',
      cta: 'Δωρεάν εγγραφή',
    },
    modal: {
      title: 'Αυτό είναι demo workspace',
      body: 'Είσαι συνδεδεμένος ως επισκέπτης, οπότε οι αλλαγές δεν αποθηκεύονται εδώ. Φτιάξε δωρεάν δικό σου workspace και ξεκίνα να ρυθμίζεις πραγματικά bots.',
      feature1: 'Διαθέσιμο Free plan — χωρίς πιστωτική κάρτα',
      feature2: 'Στήσε το bot σου σε λίγα λεπτά με το δικό σου περιεχόμενο',
      feature3: 'Συγκέντρωσε leads από την πρώτη μέρα',
      signupCta: 'Δωρεάν εγγραφή',
      keepLooking: 'Συνέχισε την εξερεύνηση',
    },
  },
  errors: {
    network: 'Σφάλμα δικτύου. Έλεγξε τη σύνδεσή σου και ξαναδοκίμασε.',
    forbidden: 'Δεν έχεις δικαίωμα για αυτή την ενέργεια.',
    notFound: 'Δε βρέθηκε.',
    server: 'Κάτι πήγε στραβά από τη μεριά μας. Δοκίμασε ξανά αργότερα.',
    unauthorized: 'Η σύνδεσή σου έληξε. Συνδέσου ξανά.',
    rateLimit: 'Πάρα πολλά αιτήματα. Περίμενε λίγο και δοκίμασε ξανά.',
  },
  validation: {
    required: 'Υποχρεωτικό πεδίο.',
    emailInvalid: 'Μη έγκυρο email.',
    minLength: 'Τουλάχιστον {n} χαρακτήρες.',
    maxLength: 'Το πολύ {n} χαρακτήρες.',
  },
  trial: {
    active: 'Δοκιμή · {days} ημ. · {used}/{limit}',
    tooltip: 'Δωρεάν δοκιμή: απομένουν {days} ημέρες, {used} από {limit} απαντήσεις',
    ended: 'Η δοκιμή έληξε',
    boxActive: 'Δοκιμή του {plan}: απομένουν {days} ημέρες · {used} από {limit} απαντήσεις.',
    boxActiveHint: 'Διάλεξε πακέτο οποτεδήποτε — ο βοηθός συνεχίζει να απαντά χωρίς διακοπή και ό,τι έστησες μένει όπως είναι.',
    boxExpired: 'Η δοκιμή έληξε — ο βοηθός δεν απαντά πλέον στο site σου.',
    boxExhausted: 'Χρησιμοποιήθηκαν και οι {limit} απαντήσεις της δοκιμής — ο βοηθός δεν απαντά πλέον στο site σου.',
    boxEndedHint: 'Ό,τι έστησες έχει κρατηθεί. Διάλεξε πακέτο και ξαναρχίζει να απαντά αμέσως.',
    sectionChoose: 'Διάλεξε πακέτο',
    activateBtn: 'Ενεργοποίηση {plan}',
    pickOption: '{plan} — δωρεάν δοκιμή {days} ημερών',
    pickHint: 'Χωρίς κάρτα. {days} ημέρες ή {responses} απαντήσεις, ό,τι έρθει πρώτο — μετά διαλέγεις πακέτο.',
  },
  bots: {
    list: {
      heading: 'Bots',
      subtitle: 'Διαχειρίσου τα chatbots, prompts και embed code.',
      subtitleLocked: 'Δες τα bots σου και αντίγραψε τον κώδικα ενσωμάτωσης.',
      newBotCta: '+ Νέο bot',
      emptyHeading: 'Κανένα bot ακόμα',
      emptyBody: 'Δημιούργησε το πρώτο σου chatbot για να αρχίσεις.',
      emptyCta: '+ Δημιούργησε το πρώτο bot',
      langCount: '{n} γλώσσα | {n} γλώσσες',
      searchPlaceholder: 'Αναζήτηση με όνομα…',
      viewCards: 'Κάρτες',
      viewList: 'Λίστα',
      noMatch: 'Κανένα bot δεν ταιριάζει με την αναζήτηση.',
      cols: { name: 'Όνομα', status: 'Κατάσταση', plan: 'Πλάνο', model: 'Μοντέλο', actions: 'Ενέργειες' },
      copyEmbedSuccess: 'Embed code αντιγράφηκε στο clipboard.\n\nLocales: {locales}',
      copyEmbedMultiNote: '(Multi-language: data-locale απαιτείται στο embed)',
      copyEmbedError: 'Σφάλμα: {msg}',
      cardActions: {
        view: '👁 Προβολή',
        settings: '⚙ Ρυθμίσεις',
        preview: 'Δοκιμή',
        embed: 'Embed code',
      },
      statusBadges: {
        pastDue: 'past due',
        cancelling: 'cancels at period end',
        managedExt: 'manual billing',
      },
    },
    create: {
      title: 'Νέο bot',
      ariaTitle: 'Νέο bot',
      closeAria: 'Κλείσιμο',
      cancel: 'Άκυρο',
      submit: 'Δημιουργία bot',
      submitting: 'Δημιουργία…',
      companyLabel: 'Εταιρεία',
      companyHint: 'Το bot ανήκει σε αυτή την εταιρεία: στο login της, στις συνομιλίες της και στη γραμμή τιμολόγησής της.',
      companyOwn: '(δική σου)',
      companyNewOption: '+ Νέα εταιρεία…',
      companyNewName: 'Όνομα νέας εταιρείας',
      companyNewParent: 'Κάτω από',
      companyNewHint: 'Η εταιρεία δημιουργείται μαζί με το bot. Στοιχεία τιμολόγησης και πρόσκληση πελάτη προσθέτεις από τη σελίδα Εταιρείες.',
      planLabel: 'Πακέτο',
      planFree: 'Free — χωρίς χρέωση όσο το στήνεις',
      planPriced: '{plan} — {price} / μήνα',
      planNoPrice: '{plan} — δεν υπάρχει ακόμα τιμή στον τιμοκατάλογό σου',
      planHint: 'Ξεκίνα με Free όσο στήνεται το bot· γυρίζει σε Pro ή Max όταν εγκατασταθεί στον πελάτη. Η τιμή «παγώνει» στο bot από τον τιμοκατάλογό σου.',
      nameLabel: 'Όνομα',
      namePlaceholder: 'π.χ. Azur Hotel',
      nameRequired: 'Δώσε όνομα στο bot σου.',
      displayNameLabel: 'Όνομα bot στο widget (προαιρετικό)',
      displayNamePlaceholder: 'π.χ. Azur Assistant',
      defaultLocaleLabel: 'Default γλώσσα',
      defaultLocaleOptions: {
        el: 'Ελληνικά (el)',
        en: 'English (en)',
        de: 'Deutsch (de)',
        fr: 'Français (fr)',
        it: 'Italiano (it)',
        es: 'Español (es)',
      },
      startMessageLabel: 'Καλωσόρισμα (start message)',
      promptLabel: 'System prompt (κοινό για όλες τις γλώσσες)',
      promptDefault: 'Είσαι βοηθός εξυπηρέτησης πελατών για την επιχείρησή μας. Απαντάς ευγενικά και βοηθάς τους πελάτες με τις ερωτήσεις τους.',
      startMessageDefault: 'Πώς μπορώ να σας βοηθήσω;',
      colorLabel: 'Χρώμα',
      positionLabel: 'Θέση',
      positions: {
        bottomRight: 'Κάτω δεξιά',
        bottomLeft: 'Κάτω αριστερά',
        bottomCenter: 'Κάτω κέντρο',
        topRight: 'Πάνω δεξιά',
        topLeft: 'Πάνω αριστερά',
        topCenter: 'Πάνω κέντρο',
      },
      launcherStyleLabel: 'Στυλ launcher',
      launcherStyles: {
        bubble: 'Bubble (κλασικό)',
        pill: 'Pill (CTA)',
        preview: 'Preview (μίνι chat)',
      },
      errorCreate: 'Σφάλμα δημιουργίας',
    },
    loading: 'Φόρτωση…',
    loadError: 'Σφάλμα φόρτωσης',
    saveError: 'Σφάλμα αποθήκευσης',
    deleteError: 'Σφάλμα διαγραφής',
    reindexError: 'Σφάλμα reindex',
    edit: {
      backAria: 'Διαδρομή',
      backToBots: 'Πίσω στα Bots',
      unsavedDot: '● Μη αποθηκευμένες αλλαγές',
      unsavedTooltip: 'Cmd/Ctrl+S για αποθήκευση',
      previewBtn: 'Δοκιμή',
      saveBtn: 'Αποθήκευση αλλαγών',
      saving: 'Αποθήκευση…',
      closeBtn: 'Κλείσιμο',
      overview: {
      title: 'Επισκόπηση Brain',
      managedBy: 'Το bot αυτό στήνεται και συντηρείται από τον πάροχό σας.',
      name: 'Όνομα',
      status: 'Κατάσταση',
      languages: 'Γλώσσες',
      installedOn: 'Εγκατεστημένο σε',
      changesTitle: 'Χρειάζεστε αλλαγή;',
      changesBody: 'Για αλλαγές στο κείμενο, στη γνώση ή στην εμφάνιση, επικοινωνήστε με τον πάροχό σας. Οι συνομιλίες, τα leads και τα στατιστικά παραμένουν πάντα διαθέσιμα σε εσάς.',
    },
    tabs: {
        overview: 'Επισκόπηση',
      general: 'Γενικά',
        prompt: 'Prompt & Model',
        widget: 'Widget',
        access: 'Πρόσβαση',
        translations: 'Γλώσσες',
        knowledge: 'Γνώση (RAG)',
        embed: 'Embed code',
        limits: 'Όρια',
        plan: 'Plan & Χρεώσεις',
        ariaLabel: 'Tabs ρυθμίσεων bot',
        unsavedAria: 'μη αποθηκευμένες αλλαγές',
        unsavedTitle: 'μη αποθηκευμένες αλλαγές',
        settingsAria: 'Κατάσταση bot',
      },
      status: {
        active: 'Ενεργό',
        draft: 'Πρόχειρο',
        paused: 'Σε παύση',
        descActive: 'Το widget απαντά σε πραγματικούς επισκέπτες',
        descPaused: 'Το widget δεν απαντά — εμφανίζει μήνυμα συντήρησης',
        descDraft: 'Δοκιμαστική κατάσταση — το widget εμφανίζεται μόνο σε δοκιμές από το dashboard',
        unsavedConfirm: 'Έχεις μη αποθηκευμένες αλλαγές. Θέλεις να φύγεις χωρίς αποθήκευση;',
      },
      general: {
        sectionBasics: 'Βασικά',
        nameInternal: 'Όνομα (εσωτερικό)',
        sectionBehavior: 'Συμπεριφορά',
        contentAware: 'Content Aware',
        contentAwareMeta: 'Στέλνει στη βοηθό τη σελίδα που βλέπει ο επισκέπτης (διεύθυνση, τίτλο, κείμενο), ώστε να απαντά για «αυτό εδώ». Γίνεται αυτόματα — δεν χρειάζεται να γράψετε κάτι στις οδηγίες.',
        localMemory: 'Local Memory',
        localMemoryMeta: 'Αποθηκεύει την συνομιλία στον browser του επισκέπτη — συνεχίζει αυτόματα όταν επιστρέψει.',
        sectionPlaceholders: 'Στοιχεία συνδεδεμένου χρήστη στις οδηγίες',
        placeholdersAuthenticated: 'Όταν ο επισκέπτης είναι συνδεδεμένος στο σύστημά σας, μπορείτε να γράψετε στις οδηγίες {names} — αντικαθίστανται με τα δικά του στοιχεία σε κάθε συνομιλία.',
        sectionNotifications: 'Ειδοποιήσεις',
        notifEmailsLabel: 'Email για lead notifications (ένα ανά γραμμή)',
        notifLocaleLabel: 'Γλώσσα ειδοποιήσεων',
        notifLocaleSite: 'Ίδια με τη γλώσσα του site',
        notifLocaleHint: 'Η γλώσσα των email που στέλνουμε σε εσάς (νέο lead, ο επισκέπτης ζήτησε άνθρωπο) — ανεξάρτητα από τη γλώσσα του site.',
        sectionDelete: 'Διαγραφή bot',
        deleteMeta: 'Το bot αρχειοθετείται. Τα δεδομένα παραμένουν στη βάση.',
        deleteBtn: 'Διαγραφή',
        deleteConfirm: 'Διαγραφή του bot "{name}"; (μπαίνει στο archive — δεν χάνονται data)',
      },
      prompt: {
        instructionsLabel: 'Instructions (κοινό σε όλες τις γλώσσες)',
        extraContextLabel: 'Επιπλέον context (προαιρετικό)',
        providerLabel: 'AI provider',
        providers: {
          gemini: 'Gemini (Google)',
          deepseek: 'DeepSeek — φθηνό + δυνατό (China hosting)',
          openai: 'OpenAI',
          anthropic: 'Anthropic (Claude)',
        },
        modelLabel: 'Model',
        modelHelpListOrCustom: 'Επιλέγεις από τη λίστα ή «Custom» για να γράψεις δικό σου model name (π.χ. preview ή beta releases).',
        customModelPrompt: 'Δώσε το model name (π.χ. gemini-experimental-1206):',
        modelOptionCustom: 'Custom — γράψε το δικό σου',
        deepseekGdprWarning: '⚠️ GDPR: Το DeepSeek φιλοξενείται στην Κίνα. Αν το bot εξυπηρετεί EU επισκέπτες, ενημέρωσε το privacy policy του site.',
        sectionParams: 'Παράμετροι',
        temperatureLabel: 'Temperature',
        temperatureMeta: 'Όσο μεγαλύτερο = πιο δημιουργικό. Default 0.45.',
        maxTokensLabel: 'Max tokens',
        maxTokensMeta: 'Μέγιστο μήκος απάντησης. Default 512.',
        replyStyleLabel: 'Μήκος απαντήσεων (κανόνας πλατφόρμας)',
        replyStyleConcise: 'Συνοπτικές (προεπιλογή)',
        replyStyleBrief: 'Πολύ σύντομες (1-3 προτάσεις)',
        replyStyleOff: 'Χωρίς κανόνα πλατφόρμας (το ύφος το ορίζει το prompt)',
        languageModeLabel: 'Γλώσσα απαντήσεων (κανόνας πλατφόρμας)',
        languageModeVisitor: 'Στη γλώσσα κάθε μηνύματος του επισκέπτη',
        languageModeLocale: 'Πάντα στη γλώσσα του widget',
        languageModeOff: 'Χωρίς κανόνα πλατφόρμας',
        languageModeMeta: 'Στη γλώσσα του επισκέπτη: ο server αναγνωρίζει τη γλώσσα κάθε μηνύματος και το ζητά ρητά στο τέλος του prompt (αν δεν είναι βέβαιος, δεν επιβάλλει τίποτα). Χωρίς κανόνα: αποφασίζει το prompt του Brain. Ορατό μόνο στη Vistoweb.',
        leadAskLabel: 'Πότε ζητά στοιχεία επικοινωνίας',
        leadAskIntent: 'Σε πρόθεση αγοράς/κράτησης/προσφοράς + όταν δεν μπορεί να βοηθήσει',
        leadAskHandoff: 'Μόνο όταν δεν μπορεί να βοηθήσει',
        leadAskNever: 'Ποτέ (κρατά μόνο ό,τι δώσει ο επισκέπτης)',
        leadAskMeta: 'Το «πότε/πώς» είναι κεντρικό· το «τι στοιχεία και για ποιο σκοπό» μένει στις οδηγίες. Ορατό μόνο στη Vistoweb.',
        personaPolicyLabel: 'Ταυτότητα & αποκάλυψη AI',
        personaPolicyPlatform: 'Πολιτική πλατφόρμας (σύσταση με όνομα· ειλικρινές «ψηφιακή βοηθός»)',
        personaPolicyOff: 'Χωρίς κανόνα πλατφόρμας (μόνο demo)',
        personaPolicyMeta: 'Το όνομα και ο ρόλος γράφονται στις οδηγίες· η πολιτική αποκάλυψης είναι κεντρική. Ορατό μόνο στη Vistoweb.',
        replyStyleMeta: 'Ο κανόνας μήκους ζει κεντρικά — ΜΗΝ τον ξαναγράφεις στο prompt. Ορατό μόνο στη Vistoweb.',
        maxHistoryLabel: 'Max history (μηνύματα)',
        maxHistoryMeta: 'Πόσα προηγούμενα turns στέλνουμε. Default 10.',
        models: {
          geminiFlashLite: 'Gemini Flash Lite (latest) — φθηνό, default',
          gemini25FlashLite: 'Gemini 2.5 Flash Lite — φθηνό σταθερό version',
          gemini20Flash: 'Gemini 2.0 Flash — γρήγορο, balanced',
          gemini25Flash: 'Gemini 2.5 Flash — με thinking, καλύτερο reasoning',
          gemini25Pro: 'Gemini 2.5 Pro — high-end, για σύνθετες ερωτήσεις',
          deepseekV4Flash: 'DeepSeek V4 Flash — φθηνό, γρήγορο',
          deepseekV4Pro: 'DeepSeek V4 Pro — δυνατό (75% off until 2026-05-05)',
          gpt4oMini: 'GPT-4o Mini — φθηνό, fast',
          gpt4o: 'GPT-4o — δυνατό, multimodal',
          claudeHaiku45: 'Claude Haiku 4.5 — φθηνό',
          claudeSonnet46: 'Claude Sonnet 4.6 — gold standard για ελληνικά',
        },
        // ---- Booking integration ----
        sectionBooking: 'Hotel Booking Deep Linking',
        bookingMeta: 'Διάλεξε τη μηχανή κρατήσεων του ξενοδοχείου. Το bot στέλνει τον επισκέπτη εκεί με ημερομηνίες και άτομα ήδη συμπληρωμένα· η ροή μπαίνει αυτόματα στο prompt.',
        bookingEngineLabel: 'Μηχανή κρατήσεων',
        bookingUrlMetaCustomPage: 'Η σελίδα κρατήσεων στο δικό σας site. Ο σύνδεσμος δίνεται αυτούσιος, χωρίς ημερομηνίες.',
        bookingNotAppointments: 'Για ραντεβού (ιατρεία, υπηρεσίες) υπάρχουν χωριστά plugins: «Σύνδεσμοι ραντεβού» (Calendly, Cal.com, Acuity) και «Ραντεβού Google Calendar».',
        sectionBookingLinks: 'Σύνδεσμοι ραντεβού',
        bookingLinksMeta: 'Το bot δίνει στον επισκέπτη σύνδεσμο για να κλείσει ραντεβού στο σύστημά σας, με το όνομα και το email του ήδη συμπληρωμένα.',
        bookingLinksProviderLabel: 'Σύστημα ραντεβού',
        bookingLinksUrlLabel: 'Σύνδεσμος του event σας',
        bookingLinksGoogleNote: 'Θέλετε το bot να βλέπει τις ελεύθερες ώρες και να κλείνει το ραντεβού απευθείας στο ημερολόγιό σας; Αυτό είναι το plugin «Ραντεβού Google Calendar» — το ενεργοποιούμε εμείς, ζητήστε το.',
        bookingUrlLabel: 'URL κρατήσεων',
        bookingUrlPlaceholder: 'https://reservations.example.com/yourhotel',
        bookingUrlMetaWebhotelier: 'Το URL σου στο reserve-online.net. Το bot θα προσθέτει αυτόματα ?checkin=…&nights=…&rooms=…&adults=…&children=…&infants=…',
        bookingUrlMetaCosmicbooker: 'Το URL σου στο CosmicBooker. Το bot θα προσθέτει αυτόματα ?checkin=…&checkout=…&guests=…',
        bookingUrlMetaCalendly: 'Πλήρες Calendly event URL (πχ https://calendly.com/onoma-sou/30min). Το pre-fill ονόματος + email προστίθεται αυτόματα.',
        bookingUrlMetaCalCom: 'Πλήρες Cal.com event URL (πχ https://cal.com/onoma-sou/30min). Το pre-fill ονόματος + email + ημερομηνίας προστίθεται αυτόματα.',
        bookingUrlMetaAcuity: 'Το Acuity scheduling URL σου. Το pre-fill ονόματος + email + τηλεφώνου προστίθεται αυτόματα.',
        bookingUrlMetaLodgify: 'Το URL της σελίδας κρατήσεων Lodgify. Ημερομηνίες άφιξης/αναχώρησης και άτομα προστίθενται αυτόματα.',
        bookingGoogleCalendarMeta: 'Εσωτερικές κρατήσεις μέσω Google Calendar — το bot διαχειρίζεται slot search + κράτηση μέσα στη συνομιλία. Σύνδεσε το Google Calendar της ομάδας σου από τα Team settings αν δεν το έχεις ήδη κάνει.',
        bookingNoneMeta: 'Χωρίς ροή κρατήσεων. Το bot δεν θα παραπέμπει σε booking link.',
        bookingUrlMissingError: 'Αυτό το engine χρειάζεται booking URL.',
        bookingSaveSuccess: 'Η ρύθμιση κρατήσεων αποθηκεύτηκε.',
        bookingSaveError: 'Δεν αποθηκεύτηκε η ρύθμιση κρατήσεων.',
        bookingEngines: {
          none: 'Κανένα',
          webhotelier: 'Web Hotelier (reserve-online.net)',
          cosmicbooker: 'CosmicBooker',
          google_calendar: 'Google Calendar (εσωτερικές κρατήσεις)',
          calendly: 'Calendly',
          cal_com: 'Cal.com',
          acuity: 'Acuity Scheduling',
          lodgify: 'Lodgify',
          custom_page: 'Δική σας σελίδα κρατήσεων',
        },
        sectionCatalog: 'Κατάλογος προϊόντων (eShop)',
        catalogMeta: 'Σύνδεσε τον ζωντανό κατάλογο του e-shop σου ώστε ο bot να απαντά live τιμές, απόθεμα και συνδέσμους προϊόντων. Τα εργαλεία αναζήτησης ενσωματώνονται αυτόματα στο prompt.',
        catalogSourceLabel: 'Πηγή καταλόγου',
        catalogApiUrlLabel: 'API URL',
        catalogApiUrlPlaceholder: 'https://yourshop.gr/modules/vistochat/vistochat.php',
        catalogTokenLabel: 'Token',
        catalogTokenPlaceholder: 'vc_yourshop_…',
        catalogTokenKeep: 'Άφησε κενό για να κρατήσεις το τρέχον token',
        catalogTokenIsSet: '✓ Υπάρχει αποθηκευμένο token (γράψε νέο για αντικατάσταση)',
        sectionTicketing: 'Κλιμάκωση σε άνθρωπο (support ticket)',
        ticketingMeta: 'Επίτρεψε στον bot να ανοίγει ticket στο helpdesk σου (Zendesk ή generic webhook) όταν ο επισκέπτης χρειάζεται άνθρωπο — με σύνοψη + το ιστορικό.',
        ticketingLocked: 'Διαθέσιμο μόνο στο πακέτο Managed — το στήνουμε εμείς πάνω στο helpdesk σας.',
        ticketingEnable: 'Ενεργοποίηση escalation tickets',
        ticketingProvider: 'Πάροχος',
        ticketingWebhook: 'Generic webhook',
        ticketingSubdomain: 'Zendesk subdomain',
        ticketingAuthType: 'Τρόπος αυθεντικοποίησης Zendesk',
        ticketingAuthBasic: 'Κλειδί API (μέχρι 30/04/2027)',
        ticketingAuthOAuth: 'Σύνδεση με Zendesk (OAuth)',
        ticketingOAuthClientId: 'OAuth Client ID (Unique Identifier)',
        ticketingOAuthClientSecret: 'OAuth Client Secret',
        ticketingOAuthRedirectUri: 'Redirect URL (για το Zendesk Admin Center)',
        ticketingOAuthConnectBtn: 'Σύνδεση με Zendesk (OAuth)',
        ticketingOAuthReconnectBtn: 'Επανασύνδεση με Zendesk (OAuth)',
        ticketingOAuthConnected: '✓ Συνδεδεμένο με Zendesk μέσω OAuth',
        ticketingOAuthNotConnected: 'Δεν έχει ολοκληρωθεί ακόμη η σύνδεση OAuth.',
        ticketingEmail: 'Email agent',
        ticketingToken: 'API token',
        ticketingWebhookUrl: 'Webhook URL',
        ticketingSecret: 'Signing secret',
        ticketingOptional: '(προαιρετικό)',
        ticketingTags: 'Tags',
        ticketingKeep: 'Άφησε κενό για να κρατήσεις την τρέχουσα τιμή',
        ticketingCredsSet: '✓ Αποθηκευμένα credentials (γράψε νέα για αντικατάσταση)',
        ticketingTest: 'Δοκιμαστικό ticket',
        ticketTestOk: '✓ Δημιουργήθηκε δοκιμαστικό ticket ({id})',
        ticketingHelp: 'Ο bot καλεί ένα εργαλείο create_ticket όταν ο επισκέπτης ζητά άνθρωπο ή έχει πρόβλημα που δεν λύνεται. Αποθήκευσε πρώτα, μετά στείλε δοκιμαστικό ticket για επαλήθευση.',
        catalogHelp: 'Το token ορίζεται στο module του site σου (vistoshop: $website_vistochat στο domain.php). Read-only πρόσβαση στον κατάλογο.',
        catalogSources: {
          none: 'Καμία',
          vistoshop: 'Vistoshop module (custom)',
          woo: 'WooCommerce',
        },
        sectionWebSearch: 'Αναζήτηση στο web',
        webSearchMeta: 'Επίτρεψε στον bot να ψάχνει ζωντανά στο web όταν η απάντηση δεν υπάρχει στη γνώση/RAG του. Παραθέτει τις πηγές που χρησιμοποίησε.',
        webSearchLocked: 'Η Αναζήτηση στο web είναι διαθέσιμη στα πλάνα Max, Managed Site και Managed eShop. Αναβάθμισε το πλάνο του bot για να την ενεργοποιήσεις.',
        webSearchEnableLabel: 'Ενεργοποίηση αναζήτησης στο web',
        webSearchModeLabel: 'Πηγές',
        webSearchModes: {
          open: 'Όλο το web (ο bot αποφασίζει)',
          allowlist: 'Προτιμώμενα domains',
        },
        webSearchDomainsLabel: 'Προτιμώμενα domains',
        webSearchDomainsPlaceholder: 'vistochat.com\nblog.vistochat.com',
        webSearchDomainsHelp: 'Ένα domain ανά γραμμή. Ο bot καθοδηγείται να προτιμά αυτές τις πηγές και παραθέτει μόνο αυτές. Σημείωση: η Google Search δεν κλειδώνεται απόλυτα σε λίστα domains, οπότε θεώρησέ το ισχυρή προτίμηση και όχι αυστηρό φίλτρο.',
        webSearchOpenHelp: 'Ο bot ψάχνει ελεύθερα όλο το web μέσω Google Search και παραθέτει τις πηγές. Προτιμά πρώτα τη δική του γνώση/RAG.',
      },
      widget: {
        sectionAppearance: 'Εμφάνιση',
        colorLabel: 'Χρώμα',
        positionLabel: 'Θέση',
        launcherStyleLabel: 'Στυλ launcher',
        launcherStyles: {
          bubble: 'Bubble (κλασικό)',
          pill: 'Pill (CTA)',
          preview: 'Preview (μίνι chat)',
        },
        themeLabel: 'Theme',
        themes: { light: 'Light', dark: 'Dark', auto: 'Auto (ακολουθεί system)' },
        animationLabel: 'Animation ανοίγματος',
        animations: { none: 'Καμία', slide: 'Slide-up', fade: 'Fade', bounce: 'Bounce' },
        autoOpenLabel: 'Auto-open delay (ms)',
        autoOpenPlaceholder: 'κενό = ποτέ',
        autoOpenMeta: 'Auto-άνοιγμα chat χωρίς click μετά από N ms. Σέβεται user-closed flag.',
        copyButtonLabel: 'Copy button σε bot messages',
        teaserRotateLabel: 'Εναλλαγή μηνυμάτων στο μπαλόνι',
        teaserRotateMeta: 'Πριν ανοίξει η συνομιλία, το μπαλόνι λέει με τη σειρά: καλωσόρισμα → βαθμολογία κριτικών → φθηνότερη απευθείας κράτηση (όσα είναι ενεργά). Ένας κύκλος, σταματά στο τελευταίο· κάθε μήνυμα ανοίγει τη συνομιλία.',
        showBrandingLabel: 'Εμφάνιση «Powered by VistoChat» στο footer',
        gdprConsentLabel: 'GDPR consent modal πριν το πρώτο μήνυμα',
        gdprConsentMeta: '⚠️ Blocking modal — ο επισκέπτης πρέπει να αποδεχθεί πριν στείλει μήνυμα. Διαφέρει από το «compliance text» (passive disclaimer κάτω από το input). Όρισε per-locale text στο tab «Γλώσσες».',
        sectionAvatar: 'Avatar',
        avatarModePreset: 'Επιλογή από βιβλιοθήκη',
        avatarModeUpload: 'Upload εικόνας',
        avatarModeUrl: 'URL',
        avatarRemove: 'Αφαίρεση',
        avatarPresetLoading: 'Φόρτωση…',
        avatarSavingPreset: 'Αποθήκευση…',
        avatarUploadPlaceholder: 'Σύρε εικόνα ή κλικ για επιλογή',
        avatarUploading: 'Ανέβασμα…',
        avatarUploadHint: 'PNG / JPG / WebP / GIF — έως 2MB. Προτείνουμε 256×256.',
        avatarUrlHint: 'HTTPS link σε εικόνα. Πρέπει να είναι publicly reachable.',
        avatarErrorLoad: 'Η εικόνα δεν φόρτωσε. Έλεγξε ότι το URL είναι HTTPS και προσβάσιμο.',
        avatarErrorPresetsLoad: 'Δεν φόρτωσαν τα presets.',
        avatarErrorPresetSave: 'Αποτυχία αποθήκευσης preset.',
        avatarErrorTooBig: 'Το αρχείο ξεπερνά τα 2MB.',
        avatarErrorMime: 'Μη υποστηριζόμενο format εικόνας.',
        avatarErrorUpload: 'Αποτυχία upload.',
        avatarErrorClear: 'Αποτυχία διαγραφής.',
        sectionCustomCss: 'Custom CSS',
        customCssMeta: 'Επιπλέον CSS που θα εφαρμοστεί μέσα στο iframe του widget. Χρησιμοποίησε το για να ταιριάξεις 100% στο brand σου.',
        customCssLockedMeta: 'Διαθέσιμο σε plans Max και Managed. Αναβάθμιση →',
        sectionCapabilities: 'Δυνατότητες',
        capLeads: {
          label: 'Auto-extract leads',
          desc: 'Αυτόματος εντοπισμός email/τηλεφώνου/ονόματος μέσα από τη συζήτηση και αποθήκευση στο /leads.',
        },
        capImageUpload: {
          label: 'Image upload',
          tagSoon: 'σύντομα',
          desc: 'Ο επισκέπτης θα μπορεί να ανεβάζει εικόνα και ο AI θα την αναλύει (Gemini multimodal).',
        },
        capFileUpload: {
          label: 'File upload',
          tagSoon: 'σύντομα',
          desc: 'Upload PDF/DOCX/TXT — ο AI θα μπορεί να απαντά για το περιεχόμενο.',
        },
        capVoiceInput: {
          label: 'Voice input',
          tagSoon: 'σύντομα',
          desc: 'Φωνητική είσοδος (mic) με transcription και προαιρετική απάντηση σε audio.',
        },
        avatarCategoryLabels: {
          face: 'Πραγματικά πρόσωπα — αλλάζουν και το launcher',
          people: 'Άνθρωποι',
          support: 'Customer Support',
          bot: 'Robots / AI',
          chat: 'Chat icons',
        },
      },
      access: {
        modeLabel: 'Ποιοι μπορούν να μιλήσουν στο bot',
        modes: {
          public: 'Public (όλοι)',
          authenticated: 'Authenticated (με JWT)',
          mixed: 'Mixed (και τα δύο)',
        },
        allowedOriginsLabel: 'URLs που επιτρέπεται να φιλοξενήσουν το widget (ένα ανά γραμμή)',
        allowedOriginsMeta: 'Άδειο = επιτρέπεται από παντού.',
      },
      translations: {
        addLocaleLabel: 'Νέα γλώσσα',
        addLocalePlaceholder: '+ προσθήκη…',
        addBtnAria: 'Πρόσθεσε γλώσσα',
        emptyHint: 'Επίλεξε γλώσσα από πάνω για να δεις/τροποποιήσεις τις ρυθμίσεις της.',
        sectionUiTexts: 'UI κείμενα στο widget',
        botDisplayNameLabel: 'Όνομα bot στο widget',
        startMessageLabel: 'Καλωσόρισμα (start message)',
        teaserLabel: 'Teaser (πάνω από το button)',
        teaserPlaceholder: 'π.χ. Χρειάζεστε βοήθεια;',
        teaserMeta: 'Άδειο = δεν εμφανίζεται teaser. Ο επισκέπτης μπορεί να το κλείσει — δεν επανεμφανίζεται.',
        teaserDelayLabel: 'Καθυστέρηση εμφάνισης (ms)',
        complianceLabel: 'Compliance text (passive, κάτω από input)',
        compliancePlaceholder: 'π.χ. Τα μηνύματα επεξεργάζονται από AI. Μη μοιράζεσαι ευαίσθητα προσωπικά στοιχεία.',
        complianceMeta: 'Άδειο = δεν εμφανίζεται. Καλό για light GDPR disclosure.',
        gdprModalLabel: 'GDPR consent modal text (blocking, πριν 1ο μήνυμα)',
        gdprModalPlaceholder: 'Άδειο = default κείμενο σε γλώσσα του visitor. HTML επιτρέπεται (π.χ. links σε privacy policy).',
        gdprModalMeta: 'Εμφανίζεται μόνο όταν το toggle στο Widget tab είναι on. HTML allowed — επιτρέπεται <a> για links σε privacy policy.',
        sectionPromptOverride: 'System prompt — μόνο για αυτή τη γλώσσα',
        promptOverrideLabel: 'Instructions για {locale}',
        promptOverridePlaceholder: 'Αν αφήσεις άδειο, χρησιμοποιείται το γενικό prompt του bot (Prompt & Model tab).',
        promptOverrideMeta: 'Άδειο = κληρονομεί από το bot-level prompt.',
        sectionKnowledgeOverride: 'Πηγές γνώσης — μόνο για αυτή τη γλώσσα',
        llmsTxtLabel: 'llms.txt URL',
        llmsTxtPlaceholder: 'https://example.com/llms.txt (άδειο = bot-level fallback)',
        sitemapLabel: 'sitemap.xml URL',
        sitemapPlaceholder: 'https://example.com/sitemap.xml (άδειο = bot-level fallback)',
        manualUrlsLabel: 'Manual URLs (ένα ανά γραμμή)',
        manualUrlsPlaceholder: 'Άδειο = bot-level fallback',
        chromaCollectionLabel: 'Chroma collection name (advanced)',
        chromaCollectionPlaceholder: 'bot_xxx (άδειο = auto-generated για το bot)',
        chromaCollectionMeta: 'Συμπλήρωσέ το μόνο αν θες αυτή η γλώσσα να αποθηκεύει chunks σε ξεχωριστή Chroma collection.',
        publishedLabel: 'Δημοσιευμένο',
        deleteLocaleBtn: 'Διαγραφή γλώσσας',
        cancelBtn: 'Άκυρο',
        saveBtn: 'Αποθήκευση μετάφρασης',
        savingBtn: 'Αποθήκευση…',
        deleteToast: 'Η μετάφραση {locale} διαγράφηκε',
      },
      knowledge: {
        loadingState: 'Φόρτωση knowledge…',
        ragHeading: 'RAG για το bot',
        ragEnableLabel: 'Ενεργό RAG (vector search σε κάθε ερώτηση)',
        ragEnableMeta: 'Σε κάθε ερώτηση γίνεται top-K retrieval από τη βάση γνώσης, φιλτραρισμένο με τη γλώσσα του visitor.',
        autoTitle: 'Αυτόματη ανανέωση',
        autoLabel: 'Ξαναδιάβασε τις πηγές κάθε εβδομάδα',
        autoMeta: 'Μία φορά την εβδομάδα, τη νύχτα, το Brain ξαναδιαβάζει το site και τις πηγές του, ώστε να ξέρει ό,τι άλλαξε. Αν το site δεν απαντά ή επιστρέψει πολύ λιγότερο περιεχόμενο από πριν, η προηγούμενη γνώση κρατιέται και ενημερώνεται η ομάδα μας.',
        autoNext: 'Επόμενη ανανέωση: περίπου στις {date}',
        autoNeedsFirst: 'Ξεκινά μετά το πρώτο χειροκίνητο reindex.',
        ragHeading2: 'RAG tuning',
        ragTuneMeta: 'Ρύθμιση πόσα και πόσο σχετικά chunks φτάνουν στον AI. Default 5 αποτελέσματα και cosine distance < 0.85.',
        topKLabel: 'Top-K (αποτελέσματα ανά ερώτηση)',
        topKMeta: 'Πόσα chunks ζητάμε από το Chroma πριν εφαρμοστεί το min score. Range 1–20.',
        minScoreLabel: 'Min score (cosine distance)',
        minScoreMeta: 'Όσο μικρότερο, τόσο πιο σχετικά πρέπει να είναι τα chunks (αυστηρότερο). Default 0.85.',
        searchModeLabel: 'Search mode',
        searchModes: {
          simple: 'Simple — μόνο το τελευταίο μήνυμα (default)',
          contextAware: 'Context-aware — συμπεριλαμβάνει conversation history (δωρεάν)',
          smart: 'Smart Search — fast LLM ξαναγράφει το query',
          smartLockedSuffix: ' (Pro+)',
        },
        searchModeMeta: 'Simple: γρήγορο αλλά χάνει context σε follow-ups (π.χ. «και τι κοστίζει αυτό;»). Context-aware: προσθέτει 2-3 προηγούμενα user messages στο embedding query — μηδαμινό κόστος, καλύτερο recall. Smart: χρησιμοποιεί fast model να ξαναγράψει την ερώτηση πριν το vector search — δραματική βελτίωση σε ασαφείς ερωτήσεις, +200 tokens/chat (~€0.00002).',
        saveRagBtn: 'Αποθήκευση RAG ρυθμίσεων',
        saveRagBtnSaving: 'Αποθήκευση…',
        fallbackHeading: 'Bot-level fallback πηγές',
        fallbackMeta: 'Χρησιμοποιούνται όταν μια γλώσσα δεν έχει δικές της πηγές (στο Translations tab).',
        saveFallbackBtn: 'Αποθήκευση fallback πηγών',
        sectionStatus: 'Status ανά γλώσσα',
        notReindexed: '· δεν έχει γίνει reindex',
        reindexAllBtn: '↻ Reindex όλων των γλωσσών',
        sectionDocuments: 'Manual documents',
        documentsMeta: 'Ανέβασε αρχεία ανά γλώσσα. Όριο 25 MiB ανά αρχείο.',
        newUploadLegend: 'Νέο upload',
        languageLabel: 'Γλώσσα',
        fileLabel: 'Αρχείο',
        uploadBtn: '↑ Ανέβασε',
        uploadingBtn: 'Upload…',
        filterLegend: 'Φίλτρο',
        allLanguages: 'Όλες',
        loadingDocs: 'Φόρτωση…',
        noDocsYet: 'Κανένα έγγραφο ακόμα.',
        deleteDocBtn: 'Διαγραφή',
        docDeletedToast: 'Το έγγραφο "{filename}" διαγράφηκε',
        errorTooLarge: 'Το αρχείο είναι πολύ μεγάλο (όριο 25 MiB).',
        errorUnsupported: 'Επιτρέπονται μόνο .pdf, .docx, .txt',
        errorMimeMismatch: 'Το περιεχόμενο δεν ταιριάζει με την επέκταση.',
        errorEmpty: 'Άδειο αρχείο',
        errorUploadGeneric: 'Σφάλμα upload',
        errorLoadKnowledge: 'Σφάλμα φόρτωσης knowledge',
        errorLoadDocuments: 'Σφάλμα φόρτωσης εγγράφων',
      },
      limits: {
        sectionBannedWords: 'Banned words',
        bannedWordsMeta: 'Λίστα όρων που, αν εμφανιστούν στο μήνυμα του επισκέπτη, μπλοκάρουν τη συνομιλία πριν φτάσει στον AI. Ένας ανά γραμμή.',
        bannedWordsLabel: 'Banned words για αυτό το bot',
        sectionBannedIps: 'Banned IPs',
        bannedIpsMeta: 'IPs ή CIDR ranges (π.χ. {single} ή {cidr}) που μπλοκάρονται. Ελέγχεται το {header} header.',
        bannedIpsLabel: 'Banned IPs για αυτό το bot',
        sectionDailyLimits: 'Ημερήσια όρια',
        dailyLimitsMeta: 'Hard limits ανά bot. 0 ή κενό = unlimited. Reset σε UTC midnight.',
        queriesPerDayLabel: 'Queries per day',
        tokensPerDayLabel: 'Tokens per day',
        sectionInputLength: 'Όριο χαρακτήρων ανά μήνυμα',
        inputLengthMeta: 'Maximum length του μηνύματος που μπορεί να στείλει ο επισκέπτης. Προστατεύει από paste-spam που αυξάνει token cost χωρίς αξία. 0 = χωρίς όριο. Default 1000 χαρακτήρες (~250 tokens).',
        inputLengthLabel: 'Input max length (χαρακτήρες)',
        inputLengthHint: 'Server επιστρέφει 400 + φιλικό μήνυμα όταν ο επισκέπτης ξεπεράσει το όριο. Το widget ενισχύει cap στο textarea + counter.',
      },
      embed: {
        sectionOptions: 'Επιλογές για το embed',
        optionsMeta: 'Παράκαμψη των bot defaults μόνο για αυτό το συγκεκριμένο embed.',
        localeOverrideLabel: 'Γλώσσα',
        positionOverrideLabel: 'Θέση',
        launcherOverrideLabel: 'Στυλ launcher',
        colorOverrideLabel: 'Χρώμα override',
        sectionSnippet: 'Snippet για paste στο site του πελάτη',
        copyBtn: '📋 Αντιγραφή',
        copiedBtn: '✓ Αντιγράφηκε!',
        sectionWhere: 'Πού μπαίνει',
        whereLines: {
          paste: 'Παστάρεται μέσα στο <head> ή πριν το </body> κάθε σελίδας.',
          wordpress: 'Σε WordPress: plugin τύπου Insert Headers and Footers, ή Yoast → Snippets, ή theme functions.php μέσω wp_footer.',
          shopify: 'Σε Shopify: Theme → Edit code → theme.liquid πριν το </body>.',
          dataLocale: 'Το data-locale είναι υποχρεωτικό μόνο όταν έχεις πάνω από 1 γλώσσα ενεργή.',
        },
        availableLocalesNote: 'Διαθέσιμα: {locales}',
      },
      plan: {
        sectionCurrent: 'Τρέχον plan',
        cycleYearly: 'ετήσιο',
        cycleMonthly: 'μηνιαίο',
        trialUntil: 'Trial μέχρι {date}',
        nextChargeOn: 'Επόμενη χρέωση: {date}',
        afterDowngrade: ' — μετά downgrade σε Free.',
        managedNote: 'Manual billing: Αυτό το bot τιμολογείται χειροκίνητα από τη Vistoweb (founding member ή custom σύμβαση). Το upgrade/cancel γίνεται από εμάς.',
        capLanguages: 'Γλώσσες',
        capLanguagesValue: 'έως {n}',
        capConversations: 'Απαντήσεις/μήνα',
        capHardSuffix: ' (hard)',
        capUnlimited: 'unlimited ({n} fair-use)',
        cancelAtEndBtn: 'Ακύρωση στο τέλος περιόδου',
        cancelling: 'Ακύρωση…',
        cancelConfirm: 'Ακύρωση συνδρομής για "{name}" στο τέλος της τρέχουσας περιόδου;',
        sectionUpgradeFree: 'Διάλεξε plan για αυτό το bot',
        sectionUpgradeChange: 'Άλλαξε plan',
        cycleAriaLabel: 'Τιμολογιακός κύκλος',
        cycleMonthlyBtn: 'Μηνιαία',
        cycleYearlyBtn: 'Ετήσια',
        cycleSaveLabel: '−33%',
        priceMonthly: '/μήνα',
        chargedYearly: 'Χρεώνεται {amount}/έτος',
        managedBundleNote: 'Setup & onboarding περιλαμβάνονται',
        capPerLang: 'Έως {n} γλώσσες',
        capWhiteLabel: 'White-label (χωρίς branding VistoChat)',
        capDiscreteBranding: 'Διακριτικό branding',
        capUnlimitedConvs: 'Unlimited απαντήσεις (fair-use)',
        capResponses: 'Έως {n} απαντήσεις / μήνα',
        capFreeSlots: '{n} δωρεάν plugins (π.χ. κρατήσεις)',
        capFreeSlotsAll: 'Όλα τα δωρεάν plugins',
        capPremiumSlots: '{n} premium plugins (Viber, Web Search, Vision…)',
        capCustomSlots: '{n} custom integrations από τη Vistoweb (π.χ. live κατάλογος e-shop, Zendesk, διαθεσιμότητα WebHotelier)',
        currentPlanBtn: 'Τρέχον plan',
        choosePlanBtn: 'Διάλεξε',
        switchPlanBtn: 'Άλλαξε σε αυτό',
        openingCheckout: 'Άνοιγμα checkout…',
        paddleNote: 'Η πληρωμή γίνεται μέσω Paddle (PCI-DSS Level 1 — δεν κρατάμε στοιχεία κάρτας). Στο checkout συμπληρώνεις VAT για B2B τιμολόγηση.',
        plans: {
          pro: { name: 'Pro', tagline: 'Σοβαρό AI assistant για ένα website.' },
          max: { name: 'Max', tagline: 'White-label + advanced analytics για ένα bot.' },
          elite: { name: 'Elite', tagline: 'Περισσότερα premium plugins και γλώσσες για απαιτητικά sites.' },
          managed: { name: 'Managed Site', tagline: 'Done-for-you για websites/υπηρεσίες. Setup περιλαμβάνεται.' },
        },
        errorCheckoutNoPaddle: 'Paddle client token δεν έχει ρυθμιστεί στο .env',
        errorCheckoutNoPrice: 'Το plan δεν έχει ρυθμισμένο Paddle price_id ακόμα. Δοκίμασε αργότερα ή επικοινώνησε με υποστήριξη.',
        errorCheckoutManaged: 'Αυτό το bot τιμολογείται χειροκίνητα από τη Vistoweb.',
        errorCheckoutGeneric: 'Σφάλμα checkout',
        partnerDiscountApplied: 'Partner Program: εφαρμόστηκε έκπτωση {percent}% ({count} paid bots).',
        launchCouponApplied: 'Εφαρμόστηκε ο κωδικός {code}: {percent}% έκπτωση στον πρώτο χρόνο! 🚀',
        errorCancel: 'Σφάλμα ακύρωσης',
      },
    },
  },
  conversations: {
    feedback: {
      title: 'Σημειώσεις & διορθώσεις',
      hint: 'Αν μια απάντηση του bot ήταν λάθος, πάτα «✎ Σχόλιο σε αυτή την απάντηση» ακριβώς κάτω από αυτήν — έτσι βλέπουμε ποια ήταν. Διαβάζουμε κάθε σημείωση και τη χρησιμοποιούμε για να το βελτιώσουμε.',
      addNoteBtn: '+ Σημείωση για όλη τη συνομιλία',
      correctBtn: '✎ Σχόλιο σε αυτή την απάντηση',
      correctHint: 'Πες μας τι ήταν λάθος σε αυτή την απάντηση και τι θα έπρεπε να πει',
      aboutLabel: 'Αφορά:',
      aboutWholeChat: 'Όλη τη συνομιλία',
      aboutAnswer: 'Απάντηση {time}: «{excerpt}»',
      linkedCount: '{n} σχόλια πάνω σε συγκεκριμένες απαντήσεις — εμφανίζονται κάτω από την καθεμία (⚑).',
      flaggedHint: 'Έχεις επισημάνει αυτή την απάντηση',
      kindNote: 'Σημείωση',
      kindCorrection: 'Διόρθωση',
      bodyPlaceholderNote: 'Τι θα θέλατε να ξέρουμε για αυτή τη συνομιλία;',
      bodyPlaceholderCorrection: 'Τι ήταν λάθος σε αυτή την απάντηση;',
      expectedPlaceholder: 'Τι θα έπρεπε να απαντήσει; (προαιρετικό, αλλά βοηθάει περισσότερο απ΄ όλα)',
      expectedLabel: 'Το σωστό:',
      empty: 'Δεν υπάρχουν σημειώσεις σε αυτή τη συνομιλία.',
      saveError: 'Δεν αποθηκεύτηκε. Δοκίμασε ξανά.',
      confirmDelete: 'Διαγραφή αυτής της σημείωσης;',
      status: {
        open: 'στάλθηκε',
        reviewed: 'το είδαμε',
        applied: 'εφαρμόστηκε',
        dismissed: 'έκλεισε',
      },
    },
    heading: 'Συνομιλίες',
    subtitle: '{total} chats από {bots} bots — επίλεξε για το πλήρες ιστορικό.',
    filters: {
      allBots: 'Όλα τα bots',
      allLeadStates: 'Όλες',
      withLead: 'Με lead',
      withoutLead: 'Χωρίς lead',
      searchPlaceholder: 'Αναζήτηση μέσα στα μηνύματα…',
    },
    empty: {
      heading: 'Καμία συνομιλία',
      body: 'Οι συνομιλίες των επισκεπτών θα φαίνονται εδώ. Αν μόλις δημιούργησες bot, πέρασε το embed code στον ιστότοπό σου.',
      cta: 'Διαχείριση bots',
    },
    table: {
      bot: 'Bot',
      started: 'Έναρξη',
      lastActivity: 'Τελ. δραστηριότητα',
      messages: 'Μηνύματα',
      tokens: 'Tokens',
      lead: 'Lead',
      lastUserQuestion: 'Τελ. ερώτηση χρήστη',
      noIp: 'no-ip',
    },
    pager: {
      prev: '‹ Προηγ.',
      next: 'Επόμ. ›',
      info: 'Σελίδα {page} / {pages} · {total} σύνολο',
    },
    drawer: {
      ariaLabel: 'Συνομιλία',
      messageCount: '{n} μηνύματα',
      loadingMessages: 'Φόρτωση μηνυμάτων…',
      titleError: 'Σφάλμα',
      pageLabel: 'Σελίδα',
      referrerLabel: 'Referrer',
      uaLabel: 'UA',
      leadLabel: 'Lead',
    },
    roles: {
      visitor: 'Επισκέπτης',
      bot: 'Bot',
      agent: 'Ομάδα (άνθρωπος)',
    },
    takeover: {
      liveBadge: 'Live',
      botHasIt: 'Το bot απαντά σε αυτή τη συνομιλία.',
      takeBtn: 'Ανάλαβε τη συνομιλία',
      releaseBtn: 'Επιστροφή στο bot',
      youHaveIt: 'Έχεις τη συνομιλία — το bot είναι σε παύση.',
      someoneHasIt: 'Άλλο μέλος της ομάδας έχει τη συνομιλία.',
      replyPlaceholder: 'Γράψε την απάντησή σου στον επισκέπτη… (Enter για αποστολή)',
      sendBtn: 'Αποστολή',
      suggestBtn: '✨ Πρόταση AI',
      suggestHint: 'Το AI συντάσσει προτεινόμενη απάντηση από τη γνώση του bot — επεξεργάσου την πριν τη στείλεις.',
    },
  },
  leads: {
    heading: 'Leads',
    subtitle: '{total} leads από {bots} bots — επίλεξε για επεξεργασία.',
    filters: {
      allBots: 'Όλα τα bots',
      allStatuses: 'Όλα τα status',
      searchPlaceholder: 'Email, τηλέφωνο, όνομα ή εταιρία…',
    },
    empty: {
      heading: 'Κανένα lead',
      body: 'Όταν επισκέπτες αφήνουν email ή τηλέφωνο στη συνομιλία, εμφανίζονται εδώ. Βεβαιώσου ότι το widget είναι εγκατεστημένο σε ζωντανό site.',
      cta: 'Διαχείριση bots',
    },
    table: {
      bot: 'Bot',
      contact: 'Στοιχεία',
      captured: 'Καταγραφή',
      status: 'Status',
      followup: 'Followup',
      chat: 'Chat',
      noContact: '(χωρίς στοιχεία)',
      msgsShort: '{n} msgs',
    },
    drawer: {
      ariaLabel: 'Lead',
      sectionRequest: 'Αίτημα (όπως επιβεβαιώθηκε στη συνομιλία)',
      sectionContact: 'Στοιχεία επικοινωνίας',
      labelName: 'Όνομα',
      labelEmail: 'Email',
      labelPhone: 'Τηλέφωνο',
      labelCompany: 'Εταιρία',
      labelLocale: 'Locale',
      labelPage: 'Σελίδα',
      labelReferrer: 'Referrer',
      sectionStatus: 'Status & Σημειώσεις',
      statusLabel: 'Status',
      statusNoteLabel: 'Σημείωση status (προαιρετικό)',
      notesLabel: 'Εσωτερικές σημειώσεις',
      followupDateLabel: 'Followup ημ/νία',
      followupStatusLabel: 'Followup status',
      followupNone: '—',
      saveBtn: 'Αποθήκευση αλλαγών',
      saving: 'Αποθήκευση…',
      sectionChat: 'Συνομιλία ({n} μηνύματα)',
      chatUnavailable: 'Η συνομιλία δεν είναι διαθέσιμη.',
      loadingChat: 'Φόρτωση…',
    },
    fallback: {
      leadId: 'Lead #{id}',
    },
    statusLabels: {
      new: 'Νέο',
      valid: 'Έγκυρο',
      test: 'Δοκιμή',
      duplicate: 'Διπλοεγγραφή',
      contacted: 'Επικοινωνία',
      qualified: 'Qualified',
      won: 'Κερδήθηκε',
      lost: 'Χάθηκε',
      spam: 'Spam',
    },
  },
  knowledge: {
    heading: 'Γνώση (RAG)',
    subtitle: '{enabled}/{total} bots ενεργά · {chunks} chunks σύνολο',
    reindexAllBtn: '↻ Reindex όλων',
    viewCards: 'Κάρτες',
    viewList: 'Λίστα',
    cols: { bot: 'Bot', rag: 'RAG', chunks: 'Chunks', locales: 'Γλώσσες', lastReindex: 'Τελ. reindex' },
    loadFailed: 'Σφάλμα φόρτωσης.',
    notLoaded: 'Δεν φορτώθηκε.',
    statusOn: 'RAG on',
    statusOff: 'RAG off',
    cardSettings: '⚙ Ρυθμίσεις',
    statChunks: 'Chunks',
    statCollection: 'Collection',
    statLastReindex: 'Last reindex',
    neverReindexed: 'ποτέ',
    reindexBtn: '↻ Reindex όλων',
    reindexing: 'Indexing…',
    sourcesLocale: 'locale sources',
    sourcesFallback: 'fallback sources',
    sourcesNone: 'no sources',
    errorNoSources: '{name}: δεν έχει configured sources. Άνοιξε Settings → Knowledge.',
    errorReindexFailed: 'reindex failed',
  },
  team: {
    heading: 'Ομάδα · {name}',
    fallbackName: 'Ομάδα',
    subtitle: 'Διαχείριση μελών και προσκλήσεων.',
    sectionMembers: 'Μέλη',
    yourRolePrefix: 'Ο ρόλος σου:',
    emptyMembers: 'Κανένα μέλος.',
    sectionNewInvite: 'Νέα πρόσκληση',
    emailLabel: 'Email',
    roleLabel: 'Ρόλος',
    onlyOwnerSuffix: '(μόνο owner)',
    submitInvite: 'Δημιουργία πρόσκλησης',
    submittingInvite: 'Δημιουργία…',
    inviteEmailUsed: 'Το email είναι ήδη μέλος της ομάδας.',
    sectionPending: 'Εκκρεμείς προσκλήσεις',
    expiresPrefix: 'λήγει',
    invitedByPrefix: 'από',
    copyInviteBtn: '📋 Αντιγραφή link',
    revokeInviteBtn: 'Ακύρωση',
    confirmRevoke: 'Ακύρωση πρόσκλησης για {email};',
    inviteCreatedToast: 'Πρόσκληση δημιουργήθηκε. Αντίγραψε τον σύνδεσμο και στείλε τον στον παραλήπτη.',
    inviteCopiedToast: 'Σύνδεσμος αντιγράφηκε για {email}.',
    inviteCopyFailed: 'Δεν επιτρέπεται η αντιγραφή — αντιγράψτε χειροκίνητα.',
    inviteCancelError: 'Σφάλμα ακύρωσης.',
    inviteCreateError: 'Σφάλμα δημιουργίας πρόσκλησης.',
    roleChangeError: 'Σφάλμα αλλαγής ρόλου.',
    roleChangedToast: 'Ο ρόλος του {email} έγινε {role}.',
    confirmLeave: 'Να αποχωρήσεις από αυτή την ομάδα;',
    leftTeamToast: 'Αποχώρησες από την ομάδα.',
    confirmTransfer: 'Μεταβίβαση ιδιοκτησίας στον/στην {email}; Θα γίνεις admin μετά.',
    transferDoneToast: 'Η ιδιοκτησία μεταβιβάστηκε στον/στην {email}.',
    transferFailed: 'Σφάλμα μεταβίβασης.',
    selfLabelLeave: 'Αποχώρηση',
    memberRemovedToast: 'Ο/η {email} αφαιρέθηκε από την ομάδα',
    inviteEmailNotSentNote: 'Email αποστολή δεν είναι ακόμα ενεργή — ο σύνδεσμος εμφανίζεται παρακάτω για χειροκίνητη κοινοποίηση.',
    joinedPrefix: 'Μπήκε',
    lastLoginPrefix: 'last login',
    transferAriaTitle: 'Μεταβίβαση ιδιοκτησίας',
    transferBtn: '→ Owner',
    removeBtn: 'Διαγραφή',
    invitableRoles: {
      owner: 'Owner',
      // "Admin" collided with the Vistoweb platform admin (users.is_admin), which
      // is a different thing entirely: permanent, account-wide, never granted from
      // this screen. Customers read the old label as "I am handing out YOUR level
      // of access". Label only — the role and its permissions are unchanged.
      admin: 'Διαχειριστής ομάδας',
      editor: 'Editor',
      agent: 'Agent',
      member: 'Member',
      viewer: 'Viewer',
    },
    roleDescriptions: {
      admin:  'Πλήρης διαχείριση ΜΕΣΑ σε αυτή την ομάδα (πλην μεταβίβασης ιδιοκτησίας) — προσθαφαιρεί μέλη, διαχειρίζεται bots & συνδρομή. Πάντα κάτω από τον Owner.',
      editor: 'Μπορεί να επεξεργάζεται bots, knowledge, conversations.',
      agent:  'Διαχειρίζεται leads & conversations αλλά δεν αλλάζει bot ρυθμίσεις.',
      member: 'Read+chat: βλέπει conversations & leads αλλά δεν τα τροποποιεί.',
      viewer: 'Read-only πρόσβαση σε όλα τα data του team.',
    },
  },
  billing: {
    platformRoot: {
      title: 'Η Vistoweb δεν τιμολογείται από κανέναν',
      body: 'Αυτή η σελίδα δείχνει σε έναν πελάτη ή συνεργάτη τι του χρεώνουμε. Για τη Vistoweb, που είναι στην κορυφή, τα ποσά των εταιρειών από κάτω είναι ΕΣΟΔΑ — τα βλέπετε ανά bot στα Managed subs, συνολικά (MRR, κόστος, περιθώριο) στα System stats και τους τιμοκαταλόγους συνεργατών στις Εταιρείες.',
      managed: 'Managed subs',
      stats: 'System stats',
      companies: 'Εταιρείες',
    },
    heading: 'Συνδρομή',
    subtitle: 'Plans, χρεώσεις και τιμολόγια. Κάθε chatbot έχει δική του συνδρομή — αναβαθμίζεις ή ακυρώνεις ξεχωριστά από τη σελίδα του.',
    sectionSummary: 'Σύνολο λογαριασμού',
    badgeSandbox: 'SANDBOX',
    cellMonthlyTotal: 'Μηνιαίο σύνολο',
    perMonth: '/μήνα',
    extManualLine: '+ {n} bot με χειροκίνητη χρέωση | + {n} bots με χειροκίνητη χρέωση',
    cellBots: 'Bots',
    free: '{n} free',
    paid: '{n} paid',
    cellPartner: 'Partner Program',
    customDiscount: 'Custom',
    customDiscountNote: '50+ bots — επικοινωνία',
    portalBtn: 'Διαχείριση μέσω Paddle (portal)',
    portalLoading: 'Άνοιγμα…',
    sectionInvoiceDetails: 'Στοιχεία τιμολόγησης',
    savedToast: 'Αποθηκεύτηκε ✓',
    invoiceIntro: 'Αν η εταιρεία σου είναι Β2Β με έγκυρο VAT, συμπλήρωσέ το ώστε στο checkout να εφαρμοστεί αυτόματα αντιστροφή χρέωσης (reverse charge) και να μη χρεωθεί τοπικός ΦΠΑ. Τα στοιχεία αποθηκεύονται για να μην χρειάζεται να ξαναγράφεις σε κάθε αναβάθμιση.',
    companyNameLabel: 'Επωνυμία εταιρείας',
    companyNamePlaceholder: 'π.χ. Promowave Ltd',
    billingEmailLabel: 'Email τιμολόγησης',
    billingEmailHint: '(προαιρετικό — η διεύθυνση email όπου θα λαμβάνεις τα τιμολόγια)',
    billingEmailPlaceholder: "billing{'@'}yourcompany.com",
    vatLabel: 'VAT / ΑΦΜ',
    vatPlaceholder: 'π.χ. EL801286009 ή GB293075488',
    countryLabel: 'Χώρα',
    countryHint: '(ISO 2-γράμματο)',
    countryPlaceholder: 'GR',
    saveBtn: 'Αποθήκευση',
    savedBtn: 'Αποθηκευμένο',
    saving: 'Αποθήκευση…',
    reverseChargeNote: '✓ Στο επόμενο checkout θα εφαρμοστεί B2B reverse-charge ({country} {vat}).',
    errorInvalidCountry: 'Δώσε ISO 2-γράμματο country code (π.χ. GR, GB, DE).',
    errorInvalidVat: 'Το VAT μπορεί να έχει μόνο γράμματα/ψηφία/κενά/τελείες/παύλες.',
    errorPortal: 'Σφάλμα portal',
    reseller: {
      title: 'Bots για τα οποία τιμολογείσαι',
      intro: 'Η Vistoweb σε τιμολογεί για όλα τα bots παρακάτω — της δικής σου εταιρείας και των {customers} πελατών σου, {bots} συνολικά. Κάθε ποσό είναι η συμφωνημένη τιμή του συγκεκριμένου bot, όχι τιμή καταλόγου.',
      ownCompany: 'η δική σου εταιρεία',
      countMeta: 'σε {companies} εταιρείες, μαζί με τη δική σου',
      unpriced: 'δεν έχει οριστεί τιμή',
      noBots: 'χωρίς ενεργό bot ακόμη',
      unpricedNote: '{n} bot δεν έχουν συμφωνημένη τιμή, οπότε ΔΕΝ μετρούν στο σύνολο. Ζήτησέ μας να οριστούν.',
    },
    sectionBots: 'Bots',
    botsEmpty: 'Κανένα bot ακόμα.',
    firstBotLink: 'Φτιάξε το πρώτο',
    monthShort: '/μήνα',
    annualLabel: '· annual',
    cancelsAt: 'cancels {date}',
    sectionPlansHeading: 'Plans (ανά chatbot)',
    managedPanel: {
      badge: 'Founding Member',
      heading: 'Πλήρως διαχειριζόμενο από τη Vistoweb',
      sub: 'Το στήνουμε, το εκπαιδεύουμε και το διαχειριζόμαστε εμείς — δεν χρειάζεται να διαλέξεις plan ή να ρυθμίσεις τίποτα.',
      save: 'γλιτώνεις {amount}/μήνα ({pct}%)',
      foot: 'Τιμολόγηση απευθείας από τη Vistoweb Ε.Ε. (ελληνικό τιμολόγιο). Για αλλαγές στο πακέτο, επικοινώνησε μαζί μας.',
    },
    cycleAriaLabel: 'Τιμολογιακός κύκλος',
    cycleMonthly: 'Μηνιαία',
    cycleYearly: 'Ετήσια',
    cycleSave: '−33%',
    plansIntro: 'Οι τιμές ισχύουν ανά chatbot, όχι ανά λογαριασμό. Φτιάξε ξεχωριστό bot για κάθε κοινό (guest support, internal staff, subscribers) και πληρώνεις μόνο για όσα ενεργοποιείς.',
    feature1lang: 'Έως {n} γλώσσα',
    featureNlang: 'Έως {n} γλώσσες',
    featureFreeMessages: '{n} απαντήσεις/μήνα',
    featureFreeBranding: 'VistoChat branding στο widget',
    featureFreeBotsCap: 'Έως 3 Free bots ανά λογαριασμό',
    featureUnlimitedFair: 'Unlimited απαντήσεις (fair-use {n}/μήνα soft cap)',
    featureWhiteLabel: 'White-label widget',
    featureDiscreetBranding: 'Διακριτικό VistoChat branding',
    featureApiWebhooks: 'API & webhooks',
    featureAdvancedAnalytics: 'Advanced analytics ({n} ημέρες ιστορικού)',
    featureBasicAnalytics: 'Basic dashboard (30 ημέρες)',
    featureCsvExport: 'CSV export στατιστικών',
    featureTopQuestions: 'Top user questions (auto-clustering)',
    featurePrioritySupport: 'Priority support',
    featureManagedService: 'Done-for-you setup + monthly tuning',
    featureFreeTrial: '{n}-day δωρεάν trial',
    setupOneTime: '+ {amount} εφάπαξ setup',
    chargedYearly: 'Χρεώνεται {amount} / έτος',
    perPeriodMonthly: '— ανά μηνιαία —',
    upgradeFromBotPage: 'Αναβαθμίζεις ξεχωριστό bot από τη σελίδα του.',
    fairUseNote: 'Fair-use: Unlimited απαντήσεις για κανονική επιχειρηματική χρήση. Bot/scraping/spam traffic δεν καλύπτονται. Πολύ μεγάλος όγκος (>5.000/μήνα ανά bot) μπορεί να απαιτεί custom plan.',
    sectionInvoices: 'Τιμολόγια',
    invoicesEmpty: 'Δεν υπάρχουν τιμολόγια ακόμα.',
  },
  stats: {
    heading: 'Στατιστικά',
    subtitle: 'Αναλυτικά charts ανά bot — σε όλα τα πλάνα.',
    botListEmpty: 'Δεν υπάρχουν ακόμα bots στην ομάδα σου.',
    newBotCta: '+ Νέο bot',
    upgradeHeading: 'Ξεκλείδωσε advanced analytics',
    upgradeBody: 'Το Free δείχνει 30 ημέρες ιστορικό. Κάθε πληρωμένο πλάνο ανεβάζει το ιστορικό στις 365 ημέρες και προσθέτει τις top ερωτήσεις των χρηστών σου.',
    upgradeFeatures: {
      daily: 'Συνομιλίες, leads, μηνύματα ανά μέρα',
      conversion: 'Conversion rate & avg response time',
      heatmap: 'Heatmap ώρα × ημέρα για να δεις πότε είναι ο peak traffic',
      csv: 'Export σε CSV για το CRM/spreadsheet σου',
    },
    seePlansCta: 'Δες τα plans →',
    botsOnFreePro: 'Bots σε Free/Pro: {names}',
    controls: {
      botLabel: 'Bot',
      botGroupCombined: 'Συνολικά',
      botAllOption: 'Όλα τα bots ({n} σύνολο)',
      botAllLabel: 'Όλα τα bots',
      botGroupAnalytics: 'Με analytics',
      botGroupLocked: 'Free / Pro (απαιτείται upgrade)',
      botLockedSuffix: '— upgrade',
      rangeLabel: 'Διάστημα',
      downloadCsv: 'Λήψη CSV',
      downloading: 'Λήψη…',
    },
    statsCards: {
      conversations: 'Συνομιλίες',
      leads: 'Leads',
      conversion: 'Conversion',
      messages: 'Μηνύματα',
      avgResponse: 'Avg response',
      bookingClicks: 'Κλικ προς κρατήσεις',
      bookingClicksMeta: '{n} συνολικά κλικ σε links',
      bookingClicksEst: '~{e} εκτίμηση ιστορικού* · {m} μετρημένα',
      bookingClicksEstTooltip: '*Εκτίμηση: 85% επί των {base} συνομιλιών όπου το bot πρότεινε κράτηση πριν την έναρξη μέτρησης (12/6/2026). Από τότε τα κλικ μετρώνται επακριβώς.',
    },
    historyClamped: 'Το ιστορικό σου περιορίζεται στις {days} ημέρες (plan {plan}).',
    chartChats: 'Συνομιλίες',
    chartChatsLeadsTitle: 'Συνομιλίες & Leads ανά ημέρα',
    chartLeads: 'Leads',
    chartUserMessages: 'Μηνύματα χρήστη',
    chartAiMessages: 'Απαντήσεις AI',
    chartMessagesTitle: 'Μηνύματα ανά ημέρα',
    sectionFunnel: 'Lead funnel',
    funnelMeta: '{leads} leads · {pct}% conversion',
    funnelEmpty: 'Δεν υπάρχουν leads στο διάστημα.',
    sectionTopPages: 'Top σελίδες έναρξης',
    topPagesMeta: 'από πού ξεκινούν οι συνομιλίες',
    topPagesEmpty: 'Δεν καταγράφηκαν page URLs (χρειάζεται Content Aware ή νεότερα chats).',
    sectionTopics: 'Κορυφαίες ερωτήσεις επισκεπτών',
    topicsMeta: 'εβδομάδα {week} · {n} μηνύματα επισκεπτών',
    topicsEmpty: 'Δεν υπάρχει ακόμα αρκετό υλικό. Τα θέματα εμφανίζονται όταν ένα bot λάβει περίπου 15 μηνύματα μέσα σε μία εβδομάδα, και ανανεώνονται κάθε Δευτέρα.',
    sectionHeatmap: 'Επισκεψιμότητα ανά ώρα/ημέρα',
    heatmapMeta: 'ώρες server (UTC) · max {n} chats',
    heatmapTooltip: '{day} {hour}:00 — {n} chats',
    errorLoadBots: 'Δεν φόρτωσε η λίστα bots.',
    errorBotPlanLocked: 'Αυτό το bot δεν περιλαμβάνει advanced analytics.',
    errorLoad: 'Σφάλμα φόρτωσης.',
    errorCsv: 'Δεν ήταν δυνατή η λήψη του CSV.',
  },
  account: {
    heading: 'Λογαριασμός',
    subtitle: 'Τα στοιχεία σου + δικαιώματα δεδομένων (GDPR).',
    sectionProfile: 'Στοιχεία λογαριασμού',
    labelName: 'Όνομα',
    labelEmail: 'Email',
    pillUnverified: 'μη επιβεβαιωμένο',
    labelLogin: 'Σύνδεση',
    loginSso: 'SSO ({provider})',
    loginPassword: 'Email + κωδικός',
    labelTeam: 'Τρέχουσα ομάδα',
    labelTermsAccepted: 'Όροι αποδοχής',
    sectionPassword: 'Αλλαγή κωδικού',
    passwordBody: 'Εδώ μένεις συνδεδεμένος· όλες οι άλλες συσκευές αποσυνδέονται. Θα σου έρθει email ειδοποίησης.',
    pwCurrent: 'Τρέχων κωδικός',
    pwNew: 'Νέος κωδικός',
    pwConfirm: 'Επανάληψη νέου κωδικού',
    pwHint: 'Τουλάχιστον 8 χαρακτήρες.',
    pwMismatch: 'Οι δύο νέοι κωδικοί δεν ταιριάζουν.',
    pwTooShort: 'Ο νέος κωδικός πρέπει να έχει τουλάχιστον 8 χαρακτήρες.',
    pwBtn: 'Αλλαγή κωδικού',
    pwSaving: 'Αποθήκευση…',
    pwDone: 'Ο κωδικός σου άλλαξε. Οι άλλες συσκευές αποσυνδέθηκαν.',
    pwError: 'Δεν ήταν δυνατή η αλλαγή του κωδικού.',
    pwSso: 'Συνδέεσαι μέσω {provider} — δεν υπάρχει κωδικός να αλλάξεις εδώ.',
    sectionExport: 'Εξαγωγή δεδομένων',
    exportBody: 'Κατέβασε ένα JSON αρχείο με όλα τα δεδομένα του λογαριασμού σου: προφίλ, ομάδες, bots που σου ανήκουν, συνομιλίες και leads. Σύμφωνα με το Άρθρο 15 + 20 GDPR.',
    exportBtn: 'Κατέβασμα JSON',
    exporting: 'Δημιουργία αρχείου…',
    errorExport: 'Σφάλμα εξαγωγής δεδομένων',
    sectionDelete: 'Διαγραφή λογαριασμού',
    deleteBody: 'Η διαγραφή είναι οριστική και μη αναστρέψιμη. Αφαιρούμε όλα τα προσωπικά σου δεδομένα από τα συστήματά μας σύμφωνα με το Άρθρο 17 GDPR. Αν είσαι owner σε ομάδα με ενεργά bots, θα σου ζητηθεί είτε να μεταβιβάσεις την ιδιοκτησία είτε να επιβεβαιώσεις ότι θες να διαγραφούν όλα μαζί με τον λογαριασμό σου.',
    deleteOpenBtn: 'Διαγραφή λογαριασμού…',
    deleteModalTitle: 'Επιβεβαίωση διαγραφής',
    deleteModalBody: 'Αφού διαγραφεί ο λογαριασμός {email}, δεν θα μπορείς να συνδεθείς ξανά με αυτά τα στοιχεία. Όλα τα προσωπικά δεδομένα διαγράφονται οριστικά.',
    deleteBlocker: {
      heading1: 'Είσαι owner σε {n} ομάδα με ενεργά bots:',
      headingN: 'Είσαι owner σε {n} ομάδες με ενεργά bots:',
      line: '{name} — {bots} bots',
      pickPrompt: 'Επιλέξτε:',
      transferLink: 'Μεταβίβαση ιδιοκτησίας →',
      transferLinkSuffix: '(πρέπει να γίνει για κάθε ομάδα ξεχωριστά)',
      orWipe: 'ή κάντε check παρακάτω και η διαγραφή θα τα σβήσει όλα μαζί.',
      acknowledge: 'Συμφωνώ να διαγραφούν και οι ομάδες + τα bots μου ({n})',
    },
    passwordLabel: 'Κωδικός',
    typeDeleteLabel: 'Πληκτρολόγησε {token} για επιβεβαίωση',
    cancelBtn: 'Ακύρωση',
    deleteBtn: 'Διαγραφή λογαριασμού',
    deleting: 'Διαγραφή…',
    errorDelete: 'Σφάλμα διαγραφής',
    closeAria: 'Κλείσιμο',
  },
  settings: {
    heading: 'Ρυθμίσεις συστήματος',
    subtitle: 'Διαχείριση master API keys και global παραμέτρων. Μόνο για super admins.',
    saveAllBtn: 'Αποθήκευση όλων',
    saving: 'Αποθήκευση…',
    noChanges: 'Δεν υπάρχουν αλλαγές.',
    savedToast: 'Αποθηκεύτηκαν.',
    groups: {
      ai: 'AI Providers',
      chroma: 'Chroma Vector DB',
      ops: 'Operational guardrails',
      billing: 'Billing (Paddle)',
    },
    pillSecret: 'secret',
    placeholderSecretWithValue: '{value} (πληκτρολόγησε νέα τιμή για αλλαγή)',
    placeholderUnsetSecret: 'Δεν έχει οριστεί κλειδί',
    placeholderUnset: 'Δεν έχει οριστεί τιμή',
    revertBtn: '↺ env',
    revertTitle: 'Επαναφορά στην τιμή του .env',
    infoCard1: {
      title: '📌 Πώς λειτουργούν οι τιμές',
      lines: {
        env: 'env: η τιμή έρχεται από το .env αρχείο στον server. Ασφαλέστερη επιλογή.',
        database: 'database: έχεις βάλει override μέσω αυτής της σελίδας. Έχει προτεραιότητα έναντι του .env.',
        unset: 'unset: δεν έχει οριστεί πουθενά. Σχετικά services δεν θα δουλεύουν.',
        revert: 'Για να καταργήσεις το database override και να γυρίσεις στο .env, πάτα το ↺ env κουμπί.',
        secrets: 'Για secrets, η εμφανιζόμενη τιμή είναι masked (πρώτα/τελευταία 4 chars). Για πλήρη τιμή, δες το .env μέσω SSH.',
      },
    },
    infoCard2: {
      title: '🔑 BYO API keys (για πελάτες)',
      intro: 'Όταν ο πελάτης φέρνει το δικό του OpenAI/Gemini/Chroma key (BYO):',
      lines: {
        schema: 'Schema έτοιμο: bots.custom_api_key_enc + bots.custom_chroma_key_enc (AES-256-GCM encrypted στη βάση)',
        ui: 'UI θα μπει στο BotsView Settings → Prompt & Model tab όταν φτιάξουμε το multi-provider framework (Backlog #8)',
        when: 'Ενεργοποίηση όταν έρθει πρώτο enterprise αίτημα — δεν αξίζει να γραφτεί χωρίς πραγματικό use case',
      },
    },
  },


  customPlugin: {
    breadcrumb: 'Custom plugin',
    catalogTitle: 'Custom plugins αυτού του Brain',
    catalogIntro: 'Λειτουργίες που φτιάξαμε ειδικά για εσάς. Δουλεύουν μόνο σε αυτό το Brain και τις ενεργοποιεί η ομάδα της Vistoweb.',
    madeFor: 'Φτιαγμένο ειδικά για αυτό το Brain από τη Vistoweb.',
    notFound: 'Αυτό το plugin δεν υπάρχει σε αυτό το Brain.',
    status: { active: 'Ενεργό', off: 'Ανενεργό', overLimit: 'Εκτός ορίου πακέτου' },
    lastRun: 'Τελευταίος έλεγχος',
    toolNote: 'Λειτουργεί μέσα στις συνομιλίες: το Brain το χρησιμοποιεί όταν χρειάζεται.',
    history: 'Ιστορικό',
    sentThisMonth: 'Απαντήσεις αυτόν τον μήνα: {n}',
    noEvents: 'Δεν έχει καταγραφεί ακόμη κάποια ενέργεια.',
    col: { date: 'Ημερομηνία', status: 'Κατάσταση', recipient: 'Παραλήπτης', subject: 'Θέμα' },
    ev: { sent: 'Στάλθηκε', skipped: 'Παραλείφθηκε', failed: 'Απέτυχε' },
    reason: {
      sender: 'άλλος αποστολέας', 'already-replied': 'έχει ήδη λάβει απάντηση', 'no-reply-to': 'χωρίς διεύθυνση ενδιαφερόμενου',
      'bad-recipient': 'μη έγκυρος παραλήπτης', 'auto-submitted': 'αυτόματο μήνυμα', precedence: 'μαζική αποστολή', spam: 'spam',
      'send-error': 'σφάλμα αποστολής — νέα προσπάθεια', 'gave-up': 'σφάλμα αποστολής — σταμάτησαν οι προσπάθειες',
    },
    badJson: 'Οι ρυθμίσεις δεν είναι έγκυρο JSON.',
    saved: 'Αποθηκεύτηκε.',
    admin: {
      title: 'Ρυθμίσεις (μόνο Vistoweb)',
      slots: 'Θέσεις custom του πακέτου: {used}/{limit}',
      enable: 'Ενεργό',
      config: 'Ρυθμίσεις (JSON)',
      secretKey: 'Μυστικό — όνομα',
      secretValue: 'Μυστικό — τιμή (μόνο εγγραφή)',
      secretKeep: 'Αποθηκευμένο — άφησε κενό για να μείνει',
      tests: 'Δοκιμές',
      testConn: 'Δοκιμή σύνδεσης',
      dryRun: 'Προσομοίωση (χωρίς αποστολή)',
      runNow: 'Εκτέλεση τώρα',
      testTo: 'Δοκιμαστική απάντηση σε (χωρίς Cc στον πελάτη)',
      sendTest: 'Αποστολή δοκιμής',
      toolInput: 'Είσοδος εργαλείου (JSON)',
      runTool: 'Δοκιμή εργαλείου',
      lastError: 'Τελευταίο σφάλμα',
    },
  },
  brain: {
    soon: 'Σύντομα',
    nav: {
      aria: 'Μενού Brain',
      allBrains: 'Όλα τα Brains',
      switch: 'Αλλαγή Brain',
      brain: 'Brain',
      channels: 'Κανάλια',
      skills: 'Skills',
      activity: 'Δραστηριότητα',
      manage: 'Διαχείριση',
      website: 'Website agent',
      viber: 'Viber agent',
      email: 'Email agent',
    },
    views: {
      overview: 'Επισκόπηση',
      knowledge: 'Γνώση',
      instructions: 'Οδηγίες',
      languages: 'Γλώσσες',
      settings: 'Ρυθμίσεις',
      plan: 'Πακέτο & Χρέωση',
      channels: 'Κανάλια',
      skillsCatalog: 'Skills',
      viberPage: 'Viber',
      emailPage: 'Email',
      website: {
        instructions: 'Οδηγίες',
        appearance: 'Εμφάνιση',
        texts: 'Κείμενα',
        access: 'Πρόσβαση & domains',
        embed: 'Κώδικας ενσωμάτωσης',
        test: 'Δοκιμή',
      },
      skills: {
        booking: 'Hotel Booking Deep Linking',
        bookinglinks: 'Σύνδεσμοι ραντεβού',
        catalog: 'Κατάλογος προϊόντων',
        ordertracking: 'Παρακολούθηση παραγγελιών',
        reviews: 'Κριτικές',
        prices: 'Σύγκριση τιμών',
        leads: 'Leads Capture',
        ticketing: 'Ticketing',
        websearch: 'Web Search',
      },
    },
    prices: {
      intro: 'Δύο πράγματα: ένα balloon δίπλα στο κουμπί του chat («Κλείστε απευθείας: συνήθως 15%+ φθηνότερα από το Booking.com» + πρόσκληση να σας πει τις ημερομηνίες του στο chat· τον σύνδεσμο κράτησης τον δίνει η βοηθός μέσα στη συνομιλία), και σύγκριση για συγκεκριμένες ημερομηνίες όταν ο επισκέπτης ρωτά τιμή μέσα στο chat.',
      honesty: 'Κανόνες που δεν αλλάζουν: σύγκριση μόνο με τα OTA που επιλέγετε· τιμές εμφανίζονται μόνο όταν η απευθείας είναι φθηνότερη από όλα· το ποσοστό του balloon βγαίνει μόνο αν ίσχυσε σε ΚΑΘΕ μέτρηση (τουλάχιστον 3) και στρογγυλεύεται προς τα κάτω.',
      label: 'Ενεργή σύγκριση τιμών',
      needsEngine: 'Χρειάζεται σύνδεσμο μηχανής κρατήσεων στο Hotel Booking Deep Linking.',
      needsBooking: 'Χρειάζεται ενεργό το Hotel Booking Deep Linking (τον σύνδεσμο της μηχανής κρατήσεων τον δίνει η βοηθός μέσα στη συνομιλία).',
      settingsTitle: 'Ρυθμίσεις',
      hotelId: 'Αναγνωριστικό Google Hotels',
      hotelIdFromReviews: 'Κενό = χρησιμοποιείται αυτό των Κριτικών.',
      hotelIdHelp: 'Βρίσκεται με την αναζήτηση ξενοδοχείου στη σελίδα Κριτικές.',
      otas: 'Σύγκριση με',
      otasHelp: 'Μόνο μεγάλα OTA. Οι μικροί aggregators συχνά δίνουν χαμηλότερες τιμές και δεν συγκρίνονται.',
      showBalloon: 'Εμφάνιση balloon δίπλα στο κουμπί του chat',
      liveCompare: 'Σύγκριση για τις ημερομηνίες του επισκέπτη μέσα στο chat',
      cap: 'Όριο συγκρίσεων ανά ημέρα',
      capHelp: 'Κάθε νέα σύγκριση κοστίζει ~0,004 $ (ίδιες ημερομηνίες μένουν 6 ώρες στη μνήμη).',
      saved: 'Αποθηκεύτηκε.',
      measureTitle: 'Εβδομαδιαία μέτρηση',
      measureHelp: 'Κάθε Δευτέρα ελέγχονται 8 μελλοντικές διαμονές 2 νυχτών (6 καθημερινές, 2 Σαββατοκύριακα) στο Google Hotels.',
      headline: 'Το balloon λέει: «συνήθως {pct}%+ φθηνότερα από το {ota}»',
      claimStale: 'Η τελευταία μέτρηση είναι παλιότερη από {days} ημέρες — το ποσοστό ΔΕΝ εμφανίζεται στο balloon ούτε το λέει η βοηθός, μέχρι να πετύχει νέα μέτρηση.',
      noHeadline: 'Δεν προκύπτει ασφαλές ποσοστό από τη μέτρηση — το balloon δεν εμφανίζεται.',
      sampleInfo: '{n} από {total} ημερομηνίες είχαν τιμές · μέτρηση {date}',
      colPairs: 'Δείγματα', colRange: 'Φθηνότερα απευθείας', colClaim: 'Ισχυρισμός',
      claimYes: 'συνήθως {pct}%+', claimNo: '— (λίγα δείγματα ή όχι πάντα φθηνότερα)',
      samplesTitle: 'Τιμές ανά ημερομηνία (ανά νύχτα)',
      colDates: 'Ημερομηνίες', colDirect: 'Απευθείας',
      samplesNote: 'Από το Google Hotels· ενδεικτικές. Κενό = δεν υπήρχαν τιμές για εκείνες τις ημερομηνίες.',
      noSample: 'Δεν έχει γίνει ακόμη μέτρηση.',
      sampleNow: 'Μέτρηση τώρα',
      samplingShort: 'Μέτρηση…',
      sampling: 'Η μέτρηση ξεκίνησε· διαρκεί έως μία ώρα. Η σελίδα θα ενημερωθεί μόνη της.',
      sampled: 'Η μέτρηση ολοκληρώθηκε.',
      schedule: 'Επαναλαμβάνεται αυτόματα κάθε Δευτέρα.',
      lastError: 'Η τελευταία μέτρηση απέτυχε ({date}) — ισχύει η προηγούμενη:',
      previewTitle: 'Τι βλέπει ο επισκέπτης',
      noPreview: 'Δεν εμφανίζεται balloon (δεν υπάρχει ασφαλές ποσοστό ή σύνδεσμος μηχανής κρατήσεων).',
      chatPreview: 'Στο chat, όταν ρωτήσει τιμή με ημερομηνίες: «Απευθείας 149 € τη νύχτα — στο Booking.com 184 €» (ενδεικτικές τιμές από Google Hotels) + σύνδεσμος κράτησης. Αν για εκείνες τις ημερομηνίες κάποιο OTA είναι φθηνότερο ή δεν υπάρχουν τιμές, δεν δίνεται καμία τιμή — μόνο ο σύνδεσμος.',
    },
    reviews: {
      intro: 'Στο chat εμφανίζεται η βαθμολογία σας· με ένα κλικ ο επισκέπτης βλέπει τις πηγές, τι αναφέρουν οι πελάτες και τις πιο πρόσφατες κριτικές. Το Brain απαντά με τα ίδια νούμερα όταν ρωτούν για την ποιότητα.',
      honesty: 'Οι κριτικές εμφανίζονται όλες, με τις νεότερες πρώτες και σύνδεσμο στην πηγή τους — δεν γίνεται επιλογή ή απόκρυψη, όπως απαιτεί η ευρωπαϊκή νομοθεσία για τους καταναλωτές.',
      label: 'Ενεργές κριτικές',
      noDataYet: 'Ενεργό, αλλά δεν έχουν φορτωθεί ακόμη κριτικές — ορίστε την πηγή και πατήστε «Ανανέωση τώρα». Μέχρι τότε δεν εμφανίζεται τίποτα.',
      sourceTitle: 'Από πού έρχονται',
      googleQuery: 'Η επιχείρηση στη Google',
      googleQueryPh: 'π.χ. Zagori Suites Βίτσα',
      googleQueryHelp: 'Όνομα και περιοχή όπως θα την έψαχνε κάποιος στους χάρτες. Μετά την πρώτη ανανέωση ελέγξτε στην προεπισκόπηση ότι βρέθηκε η σωστή επιχείρηση.',
      hotelId: 'Αναγνωριστικό Google Hotels (μόνο ξενοδοχεία)',
      hotelIdHelp: 'Προσθέτει βαθμολογία ανά θέμα (πρωινό, καθαριότητα…) και άλλες πλατφόρμες όπως το Tripadvisor. Βρείτε το με την αναζήτηση από κάτω.',
      findHotel: 'Αναζήτηση ξενοδοχείου',
      findHotelPh: 'Όνομα ξενοδοχείου',
      search: 'Αναζήτηση',
      noCandidates: 'Δεν βρέθηκε ξενοδοχείο με αυτό το όνομα.',
      manualTitle: 'Βαθμολογίες που δηλώνετε εσείς',
      manualHelp: 'Το Booking και το Expedia δεν δίνουν αυτόματα τις βαθμολογίες τους. Γράψτε τες όπως εμφανίζονται στη σελίδα σας εκεί και βάλτε τον σύνδεσμο της σελίδας σας· στον επισκέπτη φαίνονται μαζί με τις υπόλοιπες. Ενημερώνετέ τες όταν αλλάζουν — δεν ανανεώνονται αυτόματα.',
      platform: 'Πλατφόρμα', other: 'Άλλη', name: 'Όνομα', score: 'Βαθμός', outOf: 'Με άριστα', count: 'Κριτικές', url: 'Σύνδεσμος',
      addManual: 'Προσθήκη βαθμολογίας',
      statedOn: 'Δηλώθηκε στις {date}',
      refreshNow: 'Ανανέωση τώρα',
      refreshingShort: 'Ανανέωση…',
      refreshing: 'Η ανανέωση ξεκίνησε· διαρκεί 1-3 λεπτά. Η σελίδα θα ενημερωθεί μόνη της.',
      refreshed: 'Οι κριτικές ανανεώθηκαν.',
      saved: 'Αποθηκεύτηκε.',
      schedule: 'Ανανεώνεται αυτόματα μία φορά την εβδομάδα.',
      previewTitle: 'Τι βλέπουν οι επισκέπτες',
      placeCheck: 'Βεβαιωθείτε ότι αυτή είναι η δική σας επιχείρηση.',
      reviewsWord: 'κριτικές',
      stated: 'δήλωση της επιχείρησης',
      previewCounts: '{cats} θέματα · {reviews} πρόσφατες κριτικές',
      updated: 'Ενημέρωση',
      lastError: 'Η τελευταία ανανέωση απέτυχε ({date}) — εμφανίζονται τα προηγούμενα δεδομένα:',
      noSnapshot: 'Δεν έχουν φορτωθεί ακόμη κριτικές.',
      showTitle: 'Ποιες κριτικές εμφανίζονται',
      showLabel: 'Λίστα κριτικών στο chat',
      showAll: 'Όλες (οι πιο πρόσφατες)',
      show4: 'Μόνο 4★ και άνω',
      show5: 'Μόνο 5★',
      showHelp: 'Αν επιλέξετε φίλτρο, η λίστα εμφανίζεται ως «Επιλεγμένες κριτικές» με σύνδεσμο προς όλες τις κριτικές στο Google, και η συνολική βαθμολογία μένει η πραγματική (περιλαμβάνει όλες τις κριτικές). Αυτό απαιτεί η νομοθεσία για την προστασία του καταναλωτή και δεν αλλάζει.',
      showCount: 'Με αυτή την επιλογή εμφανίζονται {n} κριτικές.',
    },
    orderTracking: {
      intro: 'Όταν ο πελάτης ρωτά για μια παραγγελία, το Brain ζητά τον αριθμό παραγγελίας και το email ή το τηλέφωνο που δόθηκε σε αυτήν, και φέρνει ζωντανά από το e-shop σας την κατάσταση, την πληρωμή και την αποστολή.',
      privacy: 'Αν ο αριθμός δεν ταιριάζει με το email ή το τηλέφωνο, δεν αποκαλύπτεται τίποτα για την παραγγελία.',
      needsCatalog: 'Χρειάζεται ενεργό τον κατάλογο e-shop, γιατί χρησιμοποιεί την ίδια σύνδεση με το κατάστημά σας.',
      label: 'Ενεργή παρακολούθηση παραγγελιών',
    },
    overview: { agentsTitle: 'Agents αυτού του Brain', manage: 'Διαχείριση', noneActive: 'Κανένα ενεργό ακόμη' },
    agentInstr: {
      title: '{agent} — δικές του οδηγίες',
      introChannel: 'Προστίθενται πάνω στις κεντρικές οδηγίες του Brain και ισχύουν μόνο στις συνομιλίες στο {agent}. Δεν τις αντικαθιστούν: ό,τι πρέπει να ισχύει σε όλα τα κανάλια γράφεται στις κεντρικές, και αν τα δύο συγκρούονται, αλλάξτε τις κεντρικές.',
      introSkill: 'Προστίθενται πάνω στις κεντρικές οδηγίες του Brain και ισχύουν μόνο όταν χρησιμοποιείται το {agent}. Δεν τις αντικαθιστούν: ό,τι πρέπει να ισχύει πάντα γράφεται στις κεντρικές.',
      inactive: 'Το skill είναι απενεργοποιημένο — οι οδηγίες φυλάσσονται αλλά δεν χρησιμοποιούνται μέχρι να το ενεργοποιήσετε.',
      save: 'Αποθήκευση οδηγιών',
      saving: 'Αποθήκευση…',
      saved: 'Αποθηκεύτηκε — ισχύει από το επόμενο μήνυμα.',
      example: {
        website: 'π.χ. Όταν ο επισκέπτης είναι στη σελίδα τιμών, πρότεινε απευθείας κράτηση για την καλύτερη τιμή.',
        viber: 'π.χ. Απάντα σε 1–2 προτάσεις. Στο τέλος ρώτα αν θέλει να τον καλέσουμε.',
        email: 'π.χ. Υπόγραφε κάθε απάντηση ως «Η ομάδα κρατήσεων».',
        whatsapp: 'π.χ. Σύντομες απαντήσεις· ένας σύνδεσμος κάθε φορά.',
        leads: 'π.χ. Πριν κλείσει η συνομιλία, ζήτα τηλέφωνο για να επικοινωνήσουμε.',
        booking: 'π.χ. Ανέφερε πάντα ότι η απευθείας κράτηση στο site μας είναι 10% φθηνότερη.',
        catalog: 'π.χ. Πρότεινε πάντα κι ένα ταιριαστό αξεσουάρ μαζί με το προϊόν που ζητήθηκε.',
        ordertracking: 'π.χ. Αν η παραγγελία δεν έχει φύγει μετά από 3 εργάσιμες, δώσε το τηλέφωνο του καταστήματος.',
        reviews: 'π.χ. Όταν ρωτούν για το πρωινό, ανέφερε τι λένε οι επισκέπτες γι’ αυτό.',
        prices: 'π.χ. Όταν συγκρίνεις τιμές, ανέφερε και ότι η απευθείας κράτηση έχει δωρεάν ακύρωση.',
        ticketing: 'π.χ. Άνοιξε ticket μόνο αφού έχεις το email του πελάτη.',
        websearch: 'π.χ. Χρησιμοποίησε το web μόνο για τον καιρό και εκδηλώσεις στην περιοχή.',
      },
    },
    catalog: {
      add: 'Προσθήκη',
      addChannel: 'Προσθήκη καναλιού',
      addSkill: 'Προσθήκη skill',
      search: 'Αναζήτηση…',
      noResults: 'Δεν βρέθηκε κάτι με αυτή την αναζήτηση.',
      channelsTitle: 'Κανάλια',
      channelsIntro: 'Πού μιλάει το Brain σας με τους ανθρώπους. Κάθε κανάλι χρησιμοποιεί την ίδια γνώση, τις ίδιες οδηγίες και τα ίδια skills — το εκπαιδεύετε μία φορά.',
      skillsTitle: 'Skills',
      skillsIntro: 'Τι μπορεί να κάνει το Brain σας πέρα από το να απαντά: να κρατά στοιχεία ενδιαφερομένων, να κάνει κρατήσεις, να ψάχνει στο eShop σας, να παραδίδει σε άνθρωπο της ομάδας σας.',
      status: {
        active: 'Ενεργό',
        included: 'Περιλαμβάνεται στο πακέτο σας',
        upgrade: 'Από το {plan}',
        soon: 'Σύντομα',
        onRequest: 'Κατόπιν αιτήματος',
        full: 'Χωρίς ελεύθερη θέση',
        requires: 'Χρειάζεται: {agent}',
      },
      action: {
        configure: 'Ρυθμίσεις',
        enable: 'Ενεργοποίηση',
        upgrade: 'Αναβάθμιση πακέτου',
        request: 'Ζητήστε σύνδεση',
        disable: 'Απενεργοποίηση',
      },
      requestSubject: 'Σύνδεση {agent} στο {brain}',
      slots: {
        free: 'Δωρεάν plugins: {used} από {limit}',
        freeAll: 'Δωρεάν plugins: {used} (όλα περιλαμβάνονται)',
        premium: 'Premium plugins: {used} από {limit}',
        custom: 'Custom: {used} από {limit}',
      },
      overLimit: 'Εκτός πακέτου — δεν λειτουργεί',
    },
    agents: {
      website: { title: 'Website', desc: 'Η συνομιλία στο site σας. Απαντά στους επισκέπτες σας 24/7 από τη γνώση του Brain, στη γλώσσα τους.' },
      viber: { title: 'Viber', desc: 'Οι πελάτες σας γράφουν στο Viber και απαντά το ίδιο Brain — ίδια γνώση, ίδιοι κανόνες. Οι συνομιλίες εμφανίζονται μαζί με όλες τις άλλες.' },
      email: { title: 'Email', desc: 'Απαντά στα εισερχόμενα email του κοινού σας inbox, ή ετοιμάζει πρόχειρο για να το εγκρίνετε.' },
      whatsapp: { title: 'WhatsApp', desc: 'Συνομιλίες μέσω WhatsApp Business, με απαντήσεις από το ίδιο Brain.' },
      leads: { title: 'Leads Capture', desc: 'Εντοπίζει όνομα, email ή τηλέφωνο μέσα στη συνομιλία και σας ειδοποιεί αμέσως, ώστε κανένας ενδιαφερόμενος να μη χάνεται.' },
      booking: { title: 'Hotel Booking Deep Linking', desc: 'Στέλνει τον επισκέπτη στη μηχανή κρατήσεων του ξενοδοχείου σας με ημερομηνίες και άτομα ήδη συμπληρωμένα. Υποστηρίζει WebHotelier, CosmicBooker, Lodgify και τη δική σας σελίδα κρατήσεων.' },
      bookinglinks: { title: 'Σύνδεσμοι ραντεβού', desc: 'Δίνει στον επισκέπτη σύνδεσμο κράτησης ραντεβού στο Calendly, στο Cal.com ή στο Acuity, με τα στοιχεία του ήδη συμπληρωμένα.' },
      availability: { title: 'WebHotelier Live Availability', desc: 'Απαντά με πραγματική διαθεσιμότητα και τιμές από τη μηχανή κρατήσεων του ξενοδοχείου, αντί μόνο για σύνδεσμο κράτησης.' },
      appointments: { title: 'Ραντεβού Google Calendar', desc: 'Κλείνει ραντεβού απευθείας στο ημερολόγιό σας, βλέποντας ποιες ώρες είναι ελεύθερες. Ιδανικό για ιατρεία, δικηγόρους, υπηρεσίες.' },
      livechat: { title: 'Live chat από την ομάδα σας', desc: 'Κάποιος από την ομάδα σας αναλαμβάνει τη συνομιλία από τον πίνακα ελέγχου, με προτάσεις απάντησης από το AI. Η ειδοποίηση «ο επισκέπτης ζητά άνθρωπο» λειτουργεί πάντα, σε κάθε πακέτο.' },
      contactform: { title: 'Φόρμα επικοινωνίας στο chat', desc: 'Δομημένη φόρμα μέσα στη συνομιλία (όνομα, στοιχεία, αίτημα), που φτάνει στην ομάδα σας.' },
      sportsdata: { title: 'Αποτελέσματα αγώνων', desc: 'Ζωντανά δεδομένα ποδοσφαίρου (API-Football): βρίσκει τον αγώνα, σκορ ανά ημίχρονο/περίοδο και στατιστικά — ώστε το Brain να εξηγεί την εκκαθάριση ενός στοιχήματος με το επίσημο αποτέλεσμα.' },
      vision: { title: 'Ανάγνωση εικόνων', desc: 'Ο επισκέπτης στέλνει φωτογραφία ή screenshot και το Brain διαβάζει τι δείχνει — π.χ. ένα σφάλμα, ένα προϊόν, ένα έγγραφο.' },
      prices: { title: 'Σύγκριση τιμών', desc: 'Δείχνει στους επισκέπτες ότι η απευθείας κράτηση είναι φθηνότερη από Booking.com, Expedia κ.ά. — balloon δίπλα στο chat και σύγκριση για τις ημερομηνίες τους μέσα στη συνομιλία. Μόνο για ξενοδοχεία με μηχανή κρατήσεων.' },
      reviews: { title: 'Κριτικές', desc: 'Δείχνει στο chat τη βαθμολογία σας στη Google (και για ξενοδοχεία Tripadvisor κ.ά.) με τις πιο πρόσφατες κριτικές, και το Brain απαντά με πραγματικά νούμερα όταν ρωτούν «τι λένε οι πελάτες». Ανανεώνεται κάθε εβδομάδα.' },
      ordertracking: { title: 'Παρακολούθηση παραγγελιών', desc: 'Ο πελάτης ρωτά «πού είναι η παραγγελία μου;» και παίρνει ζωντανά την κατάσταση, την πληρωμή και την αποστολή (courier και αριθμός αποστολής), αφού επιβεβαιωθεί με email ή τηλέφωνο. Δουλεύει μαζί με τον κατάλογο e-shop.' },
      catalog: { title: 'Κατάλογος προϊόντων', desc: 'Ψάχνει ζωντανά στο eShop σας (WooCommerce / Vistoshop) και δείχνει προϊόντα με τιμή, διαθεσιμότητα και σύνδεσμο.' },
      ticketing: { title: 'Ticketing', desc: 'Όταν χρειάζεται άνθρωπος, ανοίγει ticket στο helpdesk σας (Zendesk ή webhook) με όλη τη συνομιλία.' },
      websearch: { title: 'Web Search', desc: 'Όταν η απάντηση δεν υπάρχει στη γνώση του Brain, ψάχνει ζωντανά στο web — αν θέλετε μόνο σε sites που ορίζετε — και παραθέτει πηγές.' },
    },
    viber: {
      intro: 'Αυτό το Brain απαντά στο Viber. Κάθε συνομιλία Viber εμφανίζεται στις Συνομιλίες, κι από εκεί μπορείτε να την αναλάβετε — η απάντησή σας παραδίδεται στο Viber.',
      testTitle: 'Δοκιμή Viber agent',
      testBody: 'Ανοίξτε το Viber bot της επιχείρησής σας στο κινητό και στείλτε ένα μήνυμα. Την απάντηση τη δίνει αυτό το Brain· η συνομιλία εμφανίζεται στις Συνομιλίες σε λίγα δευτερόλεπτα.',
      openConversations: 'Άνοιγμα συνομιλιών',
    },
    email: {
      title: 'Κανάλι Email (AI Auto-Reply & Drafts)',
      intro: 'Το Brain διαβάζει τα εισερχόμενα email μέσω IMAP και συντάσσει ή αποστέλλει απαντήσεις μέσω SMTP, χρησιμοποιώντας την ίδια γνώση, τους ίδιους κανόνες και τα ίδια εργαλεία (π.χ. διαθεσιμότητα WebHotelier, κατάλογο e-shop).',
      quotaBadge: 'Ενεργά mailboxes: {used} / {limit}',
      warmupPolicyTitle: 'Ασφάλεια πρώτα — Υποχρεωτικό 7ήμερο Draft Warm-up',
      warmupPolicyBody: 'Κάθε νέο mailbox ξεκινά υποχρεωτικά για τις πρώτες 7 ημέρες σε λειτουργία «Πρόχειρο (Draft)»: το AI αποθηκεύει την προτεινόμενη απάντηση στον φάκελο Drafts του webmail σας χωρίς να στέλνει email στον πελάτη, ώστε να επιβεβαιώσετε την ποιότητα στην πράξη.',
      overLimitWarning: 'Έχετε {used} ενεργά mailboxes ενώ το όριο του Brain είναι {limit}. Τα mailboxes δεν ελέγχονται μέχρι να αυξηθεί το όριο ή να απενεργοποιηθούν τα επιπλέον.',
      onRequestNote: 'Το κανάλι Email ενεργοποιείται ανά mailbox (χρέωση ανά συνδεδεμένο γραμματοκιβώτιο). Ζητήστε μας να ανοίξουμε θέση mailbox σε αυτό το Brain.',
      adminLimitLabel: 'Όριο mailboxes (Admin)',
      adminSaveLimit: 'Αποθήκευση ορίου',
      limitSaved: 'Το όριο mailboxes ενημερώθηκε.',
      mailboxesTitle: 'Συνδεδεμένα γραμματοκιβώτια (Mailboxes)',
      addMailbox: 'Προσθήκη mailbox',
      noMailboxes: 'Δεν έχει συνδεθεί ακόμη κάποιο mailbox.',
      newMailboxTitle: 'Νέο mailbox',
      editMailboxTitle: 'Επεξεργασία mailbox',
      statusEnabled: 'Ενεργό',
      statusDisabled: 'Ανενεργό',
      inWarmupBadge: 'Warm-up (Draft έως {date})',
      warmupUntil: 'Υποχρεωτικό Draft warm-up έως {date}',
      warmupCleared: 'Το 7ήμερο warm-up ολοκληρώθηκε χειροκίνητα.',
      clearWarmupBtn: 'Λήξη warm-up',
      lastOk: 'Τελευταίος έλεγχος',
      lastError: 'Τελευταίο σφάλμα ({date}):',
      enableSwitch: 'Ενεργό',
      testImapBtn: 'Έλεγχος IMAP',
      testSmtpBtn: 'Δοκιμαστικό email',
      testImapOk: 'Η σύνδεση IMAP πέτυχε ({exists} μηνύματα στο INBOX).',
      testSmtpOk: 'Η σύνδεση IMAP ({exists} μηνύματα) και η αποστολή δοκιμαστικού SMTP στο {email} πέτυχαν.',
      pollDryBtn: 'Dry-run',
      pollLiveBtn: 'Έλεγχος τώρα',
      pollDryOk: 'Dry-run ολοκληρώθηκε: ελέγχθηκαν {fetched} μηνύματα (παραλείφθηκαν {skipped}).',
      pollLiveOk: 'Ο έλεγχος ολοκληρώθηκε: {fetched} νέα · {replied} απαντήθηκαν · {drafted} πρόχειρα · {escalated} σε άνθρωπο · {skipped} παραλείφθηκαν.',
      saved: 'Το mailbox αποθηκεύτηκε.',
      deleted: 'Το mailbox διαγράφηκε.',
      deleteConfirm: 'Διαγραφή του mailbox {address};',
      recommended: 'προτείνεται',
      modes: {
        smart_hybrid: 'Smart Hybrid (Αυτόματο + Κλιμάκωση)',
        draft: 'Μόνο Πρόχειρο (Draft στον φάκελο Drafts)',
        auto_reply: 'Πλήρως Αυτόματη Απάντηση',
      },
      modeHints: {
        smart_hybrid: 'Απαντά αυτόματα σε τυπικές ερωτήσεις (τιμές, διαθεσιμότητα, ωράριο, πληροφορίες) και αποθηκεύει Πρόχειρο (Draft) + ειδοποιεί την ομάδα σας όταν υπάρχουν συνημμένα, παράπονα/ακυρώσεις, χαμηλή βεβαιότητα ή αίτημα για άνθρωπο.',
        draft: 'Δεν στέλνει ποτέ email μόνο του — αποθηκεύει την απάντηση στον φάκελο Drafts του email σας για να την ελέγξετε και να πατήσετε Αποστολή.',
        auto_reply: 'Απαντά αυτόματα σε κάθε έγκυρο εισερχόμενο email (εκτός αν ο αποστολέας ζητήσει ρητά άνθρωπο).',
      },
      stats: {
        replied: 'Απαντήθηκαν (μήνας)',
        drafted: 'Πρόχειρα / Drafts',
        escalated: 'Σε άνθρωπο',
        skipped: 'Παραλείφθηκαν',
        failed: 'Σφάλματα',
      },
      fields: {
        label: 'Ετικέτα',
        labelPh: 'π.χ. Κρατήσεις / Υποστήριξη',
        address: 'Διεύθυνση email',
        fromName: 'Όνομα αποστολέα (From)',
        fromNamePh: 'π.χ. Hotel Aurora Reservations',
        mode: 'Λειτουργία απάντησης',
        folder: 'Φάκελος IMAP',
        imapHost: 'IMAP Host',
        imapPort: 'IMAP Port',
        imapSecurity: 'Ασφάλεια IMAP',
        imapPortHint: 'Προτείνεται θύρα 143 με STARTTLS.',
        smtpHost: 'SMTP Host',
        smtpPort: 'SMTP Port',
        username: 'Username',
        password: 'Password',
        passwordKeep: 'Αποθηκευμένο — άφησε κενό για να διατηρηθεί',
        replyTo: 'Reply-To (προαιρετικό)',
        cc: 'Κοινοποίηση / Cc (χωρισμένα με κόμμα)',
        maxPerDay: 'Max απαντήσεις/ημέρα ανά αποστολέα',
        hoursFrom: 'Ώρα από (HH:MM)',
        hoursTo: 'Ώρα έως (HH:MM)',
        timezone: 'Ζώνη ώρας',
        blockedSenders: 'Εξαιρέσεις αποστολέων / domains (ένα ανά γραμμή)',
        blockedSendersHint: 'Τα auto-replies, newsletters, noreply@, mailer-daemon@ και το ίδιο το mailbox εξαιρούνται αυτόματα.',
        signatureText: 'Υπογραφή (απλό κείμενο)',
        signatureTextPh: 'Με εκτίμηση,\nΗ ομάδα μας',
        signatureHtml: 'Υπογραφή (HTML, προαιρετικό)',
      },
      eventsTitle: 'Πρόσφατη δραστηριότητα Email',
      eventsPrivacyNote: 'Καταγράφονται μόνο μεταδεδομένα (θέμα, domain αποστολέα, κατάσταση). Το περιεχόμενο της συνομιλίας εμφανίζεται στις Συνομιλίες και ακολουθεί την πολιτική διατήρησης δεδομένων.',
      allStatuses: 'Όλες οι καταστάσεις',
      noEvents: 'Δεν έχουν καταγραφεί ακόμη συμβάντα email.',
      colDate: 'Ημερομηνία',
      colMailbox: 'Mailbox',
      colSubject: 'Θέμα',
      colSender: 'Αποστολέας',
      colStatus: 'Κατάσταση',
      colReason: 'Λεπτομέρειες',
    },
    instructions: {
      title: 'Κεντρικές οδηγίες',
      intro: 'Η κεντρική εκπαίδευση του Brain. Ισχύει σε κάθε κανάλι — site, Viber και όσα προστεθούν. Οδηγίες ανά γλώσσα ορίζεις στις Γλώσσες.',
      perAgentTitle: 'Επιπλέον οδηγίες ανά κανάλι ή skill',
      perAgentIntro: 'Αυτά έχουν και δικές τους οδηγίες, που ισχύουν μόνο εκεί (π.χ. μόνο στο Viber) και προστίθενται στις κεντρικές. Πατήστε για να τις δείτε ή να τις αλλάξετε.',
      perAgentHint: 'Θέλετε κάτι να ισχύει μόνο σε ένα κανάλι ή skill — π.χ. πιο σύντομες απαντήσεις μόνο στο Viber; Γράψτε το στη σελίδα εκείνου του καναλιού ή skill· προστίθεται στις κεντρικές οδηγίες μόνο εκεί.',
    },
    languages: { intro: 'Οι γλώσσες του Brain. Για κάθε γλώσσα: οδηγίες ειδικά για αυτή και από πού μαθαίνει (site, sitemap).' },
    texts: { intro: 'Ό,τι βλέπει ο επισκέπτης στο widget, ανά γλώσσα: μήνυμα έναρξης, placeholder, teaser, κείμενο GDPR.' },
    leads: { intro: 'Εντοπίζει email, τηλέφωνο και όνομα μέσα στη συνομιλία και ειδοποιεί την ομάδα σου.' },
    test: {
      title: 'Δοκιμή Website agent',
      intro: 'Το widget ακριβώς όπως το βλέπουν οι επισκέπτες σου. Οι δοκιμαστικές συνομιλίες εμφανίζονται στις Συνομιλίες.',
      locale: 'Γλώσσα',
      restart: 'Νέα συνομιλία',
    },
  },
  companies: {
    billing: {
      heading: 'Τιμολόγηση συνεργάτη',
      note: 'Πώς πληρώνονται τα bots κάτω από αυτή την εταιρεία. Ισχύει για bots που ανοίγουν από εδώ και πέρα· τα υπάρχοντα δεν μετατρέπονται.',
      label: 'Τιμολόγηση (αν η εταιρεία είναι συνεργάτης)',
      none: 'Δεν είναι συνεργάτης',
      vistoweb: 'Από εμάς (μηνιαίο τιμολόγιο)',
      paddle: 'Μέσω Paddle (αυτοεξυπηρέτηση)',
      createHint: 'Από εμάς: ο συνεργάτης διαλέγει Free / Pro / Max ανά bot και παίρνει το μηνιαίο τιμολόγιό μας.',
    },
    create: {
      cta: '+ Νέα εταιρεία',
      title: 'Νέα εταιρεία',
      name: 'Όνομα εταιρείας',
      namePlaceholder: 'π.χ. Hotel Aurora',
      nameRequired: 'Γράψε το όνομα της εταιρείας.',
      parent: 'Κάτω από',
      ownerHint: 'Η εταιρεία ανοίγει με ιδιοκτήτη την εταιρεία από πάνω. Πρόσκαλεσε τον πελάτη από τα Μέλη και μεταβίβασε την ιδιοκτησία όταν είναι έτοιμος.',
      invoicingHeading: 'Στοιχεία τιμολόγησης (προαιρετικά)',
      submit: 'Δημιουργία εταιρείας',
      cancel: 'Ακύρωση',
      exists: 'Υπάρχει ήδη εταιρεία με αυτό το όνομα εδώ.',
      forbidden: 'Νέα εταιρεία ανοίγει μόνο κάτω από εταιρεία με συμφωνημένο τιμοκατάλογο.',
    },
    locks: {
      heading: 'Αυτοεξυπηρέτηση',
      note: 'Απενεργοποίησε ό,τι μπορεί να αλλάζει μόνη της αυτή η εταιρεία. Συνομιλίες, leads και στατιστικά δεν κλειδώνονται ποτέ.',
      readonlyNote: 'Ορίζεται από την εταιρεία από πάνω.',
      area: { bot_settings: 'Ρυθμίσεις bot', knowledge: 'Γνώση', team: 'Ομάδα' },
      hint: {
        bot_settings: 'Κλειδωμένο: δεν αλλάζουν prompt, μοντέλο ή widget.',
        knowledge: 'Κλειδωμένο: δεν αλλάζουν πηγές γνώσης ούτε κάνουν reindex.',
        team: 'Κλειδωμένο: δεν προσθέτουν ούτε αφαιρούν μέλη.',
      },
    },
    prices: {
      heading: 'Τι πληρώνει αυτή η εταιρεία, ανά πακέτο',
      note: 'Ανά bot, τον μήνα. Ένα bot που έχει ήδη συνδεθεί κρατά την τιμή με την οποία συνδέθηκε — η νέα τιμή ισχύει μόνο για νέες συνδέσεις.',
      unset: 'Δεν ορίστηκε',
      edit: 'Επεξεργασία',
      editNote: 'Η νέα τιμή ισχύει για bots που συνδέονται από αυτή την ημερομηνία. Όσα έχουν ήδη συνδεθεί κρατούν παγωμένη την τιμή τους — γι᾽ αυτό μια αλλαγή μπορεί να προγραμματιστεί με ασφάλεια.',
      scheduled: 'Αλλάζει σε {amount} στις {date}',
      amount: 'Ποσό ανά bot / μήνα (EUR)',
      effectiveFrom: 'Ισχύει από',
      noteLabel: 'Εσωτερική σημείωση',
      invalidAmount: 'Δώσε έγκυρο ποσό.',
      noPriceList: 'Αποθηκεύτηκε. Καμία εταιρεία από αυτή και πάνω δεν έχει τιμοκατάλογο, οπότε το ποσό έμεινε ακαθόριστο — όρισέ το χειροκίνητα στη χειροκίνητη τιμολόγηση.',
      noListEntry: 'Αποθηκεύτηκε, αλλά ο τιμοκατάλογος δεν έχει εγγραφή για {plan} — το ποσό έμεινε ΑΚΑΘΟΡΙΣΤΟ αντί να δειχθεί 0. Όρισε την τιμή παραπάνω.',
    },
    includes: {
      languages: '{value} γλώσσες',
      responses_per_month: '{value} απαντήσεις / μήνα',
      responses_unlimited: 'Απεριόριστες απαντήσεις',
      analytics: 'Στατιστικά — ιστορικό {value} ημερών',
      analytics_export: 'Εξαγωγή CSV',
      analytics_topics: 'Top ερωτήσεις χρηστών',
      white_label: 'White label (χωρίς branding VistoChat)',
      discreet_branding: 'Διακριτικό branding',
      api_and_webhooks: 'API και webhooks',
      rag_smart_search: 'Έξυπνη αναζήτηση στη βάση γνώσης',
      web_search: 'Αναζήτηση στο web',
      booking_api: 'Ζωντανή διαθεσιμότητα και τιμές',
      priority_support: 'Υποστήριξη με προτεραιότητα',
    },
    caveats: { webhotelier_only: '(μόνο μηχανές WebHotelier)' },
  },

  admin: {
    feedback: {
      tabFeedback: "Σχόλια",
      tabProposals: "Προτάσεις prompt",
      pickBot: "Διάλεξε bot…",
      generateBtn: "Δημιουργία πρότασης",
      modelSummary: "Τι εντόπισε το μοντέλο",
      sectionLabel: "Κείμενο που θα γραφτεί",
      writesToBase: "στο base prompt",
      writesToLocales: "στα ανά-γλώσσα prompts ({l})",
      reviewWarning: "Διάβασέ το πριν εγκρίνεις. Το μοντέλο μπορεί να εκλάβει μια απόπειρα injection ως πραγματικό περιστατικό, ή να γενικεύσει πέρα από όσα είπε η ανατροφοδότηση. Επεξεργάσου ελεύθερα — ό,τι είναι στο πλαίσιο είναι ακριβώς ό,τι θα γραφτεί, και μόνο μέσα στο marker block.",
      applyBtn: "Έγκριση & εφαρμογή",
      rejectBtn: "Απόρριψη",
      confirmApply: "Να γραφτεί αυτή η ενότητα στο prompt του {bot};",
      applyError: "Δεν εφαρμόστηκε.",
      digestError: "Δεν δημιουργήθηκε πρόταση.",
      noProposals: "Καμία πρόταση.",
      fromN: "από {n} σχόλια",
      backupAt: "Backup:",
      statusLabel: "Κατάσταση",
      proposalStatus: { draft: "Προσχέδια", applied: "Εφαρμοσμένα", rejected: "Απορριφθέντα" },
      title: 'Σχόλια πελατών',
      subtitle: 'Σημειώσεις και διορθώσεις που άφησαν πελάτες σε συνομιλίες. Οι διορθώσεις έχουν δίπλα-δίπλα την απάντηση του bot και το τι θα έπρεπε να πει — αυτό το ζευγάρι διορθώνει το prompt.',
      botSaid: 'Το bot απάντησε',
      customerSaid: 'Ο πελάτης λέει',
      internalPlaceholder: 'Εσωτερική σημείωση (δεν τη βλέπει ο πελάτης) — αποθηκεύεται όταν φύγεις από το πεδίο',
      searchPlaceholder: 'Αναζήτηση στις σημειώσεις…',
      count: '{n} συνολικά',
      empty: 'Καμία εγγραφή σε αυτή την ουρά.',
      loadError: 'Δεν φορτώθηκε η ουρά.',
      saveError: 'Δεν αποθηκεύτηκε.',
      filters: {
        open: 'Ανοιχτά', reviewed: 'Ελεγμένα', applied: 'Εφαρμοσμένα', dismissed: 'Κλειστά',
        all: 'Όλες οι καταστάσεις', allKinds: 'Όλα τα είδη',
      },
    },
    common: {
      onlyAdmin: 'Μόνο admin Vistoweb.',
      errorAccess: 'Δεν έχεις admin πρόσβαση.',
      errorLoad: 'Σφάλμα φόρτωσης.',
      loading: 'Φόρτωση…',
      saving: 'Αποθήκευση…',
      close: 'Κλείσιμο',
    },
    users: {
      title: 'Χρήστες',
      subtitle: 'Όλοι οι χρήστες του συστήματος, με ομάδες, ρόλους και τελευταία σύνδεση.',
      searchPlaceholder: 'Αναζήτηση με όνομα, email ή ομάδα…',
      showing: '{n} από {total}',
      noMatch: 'Κανένας χρήστης δεν ταιριάζει με την αναζήτηση.',
      stats: { total: 'Σύνολο', verified: 'Επιβεβαιωμένοι', loggedIn: 'Με σύνδεση', admins: 'Admins' },
      cols: { user: 'Χρήστης', teams: 'Ομάδες / Ρόλοι', verified: 'Επιβεβ.', lastLogin: 'Τελ. σύνδεση' },
    },
    companies: {
      heading: 'Εταιρείες',
      subtitle: 'Κάθε εταιρεία που διαχειρίζεσαι: στοιχεία τιμολόγησης, άνθρωποι, bots, και τι μπορεί να αλλάζει μόνη της.',
      searchPlaceholder: 'Αναζήτηση εταιρείας, ΑΦΜ ή owner…',
      rowCount: '{n} εταιρείες',
      manageBtn: 'Διαχείριση',
      empty: 'Δεν βρέθηκαν εταιρείες.',
      summary: {
        companies: 'Εταιρείες',
        members: 'Μέλη σύνολο',
        bots: 'Bots σύνολο',
      },
      table: {
        team: 'Ομάδα',
        legalName: 'Επωνυμία',
        vat: 'ΑΦΜ',
        owner: 'Owner',
        members: 'Μέλη',
        bots: 'Bots',
        sellsTo: 'πουλά σε {n}',
      noLegal: '— δεν έχει οριστεί —',
      },
      identity: {
        heading: 'Στοιχεία τιμολόγησης',
        hint: 'Η νομική οντότητα που τιμολογείται. Χρησιμοποιείται σε Paddle B2B reverse-charge + στα χειροκίνητα τιμολόγια.',
        legalName: 'Επωνυμία (νομική οντότητα)',
        legalNamePlaceholder: 'π.χ. CRYSTAL ΒΟΥΛΙΑΓΜΕΝΗ ΞΕΝ.ΜΟΝ ΙΚΕ',
        address: 'Διεύθυνση',
        addressPlaceholder: 'π.χ. Δεκελείας 10 & Δανάης, 166 71',
        vat: 'ΑΦΜ',
        country: 'Χώρα (ISO-2)',
        billingEmail: 'Email τιμολόγησης',
        save: 'Αποθήκευση στοιχείων',
        saved: 'Αποθηκεύτηκε',
        errorSave: 'Δεν αποθηκεύτηκαν τα στοιχεία.',
      },
      bots: {
        heading: 'Bots',
        none: 'Η εταιρεία δεν έχει ακόμα bots.',
        bot: 'Bot',
        status: 'Κατάσταση',
        plan: 'Πακέτο',
        chunks: 'Chunks',
        monthly: 'Μηνιαίως (χωρίς ΦΠΑ)',
        open: 'Άνοιγμα',
        extHint: 'Τιμολογείται από τη Vistoweb εκτός Paddle',
      },
      members: {
        heading: 'Μέλη ομάδας',
        user: 'Χρήστης',
        role: 'Ρόλος',
        lastLogin: 'Τελ. σύνδεση',
        makeOwner: 'Κάν’ τον owner',
        remove: 'Αφαίρεση',
        resetPassword: 'Reset κωδικού',
        pending: 'Εκκρεμείς προσκλήσεις',
        copyLink: 'Αντιγραφή link',
        copyPass: 'Αντιγραφή',
        copied: 'Αντιγράφηκε ✓',
        revoke: 'Ακύρωση',
        invitePlaceholder: 'email νέου μέλους',
        invite: 'Πρόσκληση',
        inviteHint: 'Η πρόσκληση στέλνεται με email· το μέλος μπαίνει στην ομάδα μόλις την αποδεχτεί.',
        confirmOwner: 'Μεταφορά ιδιοκτησίας στον/στην {email}; Ο τρέχων owner γίνεται admin.',
        confirmRemove: 'Αφαίρεση του/της {email} από την ομάδα;',
        confirmRevoke: 'Ακύρωση της πρόσκλησης προς {email};',
        confirmReset: 'Δημιουργία νέου κωδικού για τον/την {email}; Όλες οι ανοιχτές συνεδρίες θα αποσυνδεθούν.',
        resetDone: 'Νέος κωδικός για {email} — αντίγραψέ τον τώρα, δεν θα ξαναεμφανιστεί:',
        resetWarn: 'Στείλ’ τον στον πελάτη με ασφαλή τρόπο. Ο χρήστης πρέπει να συνδεθεί ξανά με αυτόν τον κωδικό.',
        errorAction: 'Η ενέργεια απέτυχε.',
        errorInvite: 'Η πρόσκληση απέτυχε.',
        roles: {
          owner: 'Owner',
          admin: 'Admin',
          editor: 'Editor',
          agent: 'Agent',
          member: 'Member',
          viewer: 'Viewer',
        },
      },
    },
    stats: {
      title: 'Στατιστικά συστήματος',
      heading: 'Admin · System stats',
      subtitle: 'Vistoweb internal — έσοδα, κόστη, περιθώρια & churn signals για όλα τα teams.',
      money: {
        mrr: 'MRR (Paddle)',
        mrrSub: 'μηνιαίο recurring (Paddle subs)',
        outOfBand: 'Out-of-band (Vistoweb)',
        outOfBandSub: '{n} bots · χειροκίνητη τιμολόγηση/μήνα',
        totalMonthly: 'Έσοδα (μηνιαία)',
        totalMonthlySub: 'MRR Paddle + out-of-band',
        paidWindow: 'Πληρωμές στο διάστημα',
        paidWindowSub: '{days} ημέρες · πραγματικές εισπράξεις Paddle',
        llmCost: 'LLM cost (διάστημα)',
        llmCostSub: 'tokens που κατανάλωσαν όλα τα bots',
        grossMargin: 'Μικτό περιθώριο (μηνιαίο, εκτ.)',
        grossMarginSub: 'μηνιαία έσοδα − μηνιαίο LLM cost (run-rate)',
      },
      stats: {
        chats: 'Συνομιλίες',
        leads: 'Leads',
        conversion: 'Conversion',
        messages: 'Μηνύματα',
        teams: 'Teams',
        teamsSub: '{paid} paid · {trialing} trial · {atRisk} at risk',
      },
      planDistribution: 'Plan distribution',
      planTotal: '{n} bots συνολικά',
      subHealth: 'Subscription health',
      perTeam: 'Per-team revenue / cost / margin',
      perTeamMeta: '{n} teams με κίνηση',
      noActivity: 'Καμία κίνηση στο διάστημα.',
      topBots: 'Top bots κατά LLM cost',
      topBotsMeta: 'top 20 — δες ποιοι «καίνε» tokens',
      noTokenCost: 'Καμία χρέωση token στο διάστημα.',
      table: {
        team: 'Team',
        bots: 'Bots',
        chats: 'Chats',
        leads: 'Leads',
        cost: 'Κόστος/μήνα',
        revenue: 'Έσοδα/μήνα',
        margin: 'Περιθώριο/μήνα',
        bot: 'Bot',
        plan: 'Plan',
        msgs: 'Msgs',
      },
      sub: {
        none: 'no sub',
        trial: 'trial',
        active: 'active',
        pastDue: 'past_due',
        paused: 'paused',
        cancelled: 'cancelled',
        expired: 'expired',
      },
      extPillTitle: 'Vistoweb χειροκίνητη τιμολόγηση',
    },
    managed: {
      title: 'Managed χρεώσεις',
      heading: 'Admin · Managed subscriptions',
      subtitle: 'Vistoweb invoiced — billing terms, ποσά προ ΦΠΑ, ημερομηνίες τιμολόγησης. Δεν περνάει από Paddle.',
      summary: {
        managedBots: 'Managed bots',
        managedBotsSub: 'externally invoiced',
        monthlyEx: 'Σύνολο μηνός (προ ΦΠΑ)',
        monthlyExSub: 'net recurring',
        monthlyInc: 'Σύνολο μηνός (με ΦΠΑ)',
        monthlyIncSub: 'gross invoiced',
      },
      reminders: {
        overdueTitleOne: '{n} bot OVERDUE',
        overdueTitleMany: '{n} bots OVERDUE',
        todayTitleOne: 'Σήμερα να τιμολογήσεις: {n} πελάτη',
        todayTitleMany: 'Σήμερα να τιμολογήσεις: {n} πελάτες',
        weekTitle: 'Επόμενες 7 ημέρες: {n} τιμολόγια',
        total: 'Σύνολο: {ex} προ ΦΠΑ · {inc} με ΦΠΑ',
        daysLateOne: '{n} μέρα καθυστέρηση',
        daysLateMany: '{n} μέρες καθυστέρηση',
        exVatSuffix: 'προ ΦΠΑ',
      },
      filter: {
        managedOnly: 'Managed only',
        all: 'All bots',
        rowCount: '{n} εγγραφές',
      },
      table: {
        customer: 'Customer / Bot',
        plan: 'Plan',
        monthlyEx: 'Μηνιαίο (προ ΦΠΑ)',
        monthlyInc: 'Με ΦΠΑ',
        setup: 'Setup',
        billing: 'Τιμολόγηση',
        startedAt: 'Έναρξη',
        nextInvoice: 'Επόμενο',
        notes: 'Σημειώσεις',
        billingDay: 'Ημέρα {n}',
        listPrice: 'list price',
        vatPctSuffix: '% ΦΠΑ',
        empty: 'Δεν υπάρχουν εγγραφές.',
        editBtn: 'Edit',
      },
      modal: {
        editAria: 'Επεξεργασία {team} · {bot}',
        closeAria: 'Κλείσιμο',
        manageExt: 'Διαχείριση εκτός Paddle (Vistoweb invoicing)',
        manageExtHint: 'Ενεργό = το bot τιμολογείται χειροκίνητα από Vistoweb. Paddle upgrade/cancel απενεργοποιείται.',
        plan: 'Plan',
        monthly: 'Μηνιαίο ποσό προ ΦΠΑ (€)',
        monthlyPlaceholder: 'κενό = list price',
        monthlyHint: 'Άσε κενό για να χρησιμοποιηθεί η default τιμή του plan.',
        setup: 'Setup προ ΦΠΑ (€)',
        vat: 'ΦΠΑ %',
        billingDay: 'Ημέρα τιμολόγησης (1-28)',
        billingDayPlaceholder: 'π.χ. 5',
        billingDayHint: 'Cap στο 28 για αποφυγή προβλημάτων με Φεβρουάριο.',
        startedAt: 'Ημερομηνία έναρξης',
        notes: 'Σημειώσεις (admin-only)',
        notesPlaceholder: 'Π.χ. αρχική προσφορά, contact person, special terms…',
        cancel: 'Ακύρωση',
        save: 'Αποθήκευση',
        saving: 'Αποθήκευση…',
        errorSave: 'Αποτυχία αποθήκευσης.',
      },
    },
    usage: {
      title: 'Χρήση',
      heading: 'Admin · Χρήση & Κόστη',
      subtitle: 'Vistoweb internal — credits consumption per bot, model, provider. Helps spot overusage και αξιολόγηση μοντέλων.',
      coverage: {
        label: 'Coverage:',
        coveragePct: '{covered} / {total} assistant messages έχουν cost data ({pct}%).',
        legacyEstimate: '{n} legacy WP messages: εκτίμηση μέσω blended rate (80% input / 20% output).',
        dateRange: 'Δεδομένα: {from} → {to}.',
      },
      kpi: {
        totalCost: 'Κόστος μοντέλων AI (διάστημα)',
        totalCostSub: '{days} ημέρες',
        avgDay: 'Μ.Ο. ανά ημέρα',
        avgDaySub: 'avg cost/day',
        projected: 'Projected/μήνα',
        projectedSub: 'βάσει τρέχοντος ρυθμού',
        activeBots: 'Ενεργά bots',
        activeBotsSub: '{messages} μηνύματα',
      },
      costPerDay: 'Κόστος ανά ημέρα (stacked by provider)',
      noData: 'Δεν υπάρχουν δεδομένα στο επιλεγμένο διάστημα.',
      chroma: {
        heading: '🟣 Chroma Cloud — Starter (usage-based)',
        chunks: 'chunks αποθηκευμένα',
        chunksSub: '~13 MiB storage σε 9 collections',
        reads: 'reads (στο διάστημα)',
        writes: 'writes ({n} reindex events)',
        manual: 'manual',
        manualLabel: 'cost source',
        manualSub: 'βλ. πραγματικά τιμολόγια παρακάτω',
      },
      pnl: {
        heading: '📊 Αποτέλεσμα ανά μήνα — έσοδα και πλήρες κόστος',
        intro: 'Έσοδα από τα βιβλία (περίοδος υπηρεσίας). Κόστος: τιμολόγια όπου υπάρχουν, αλλιώς μετρήσεις ή εκτίμηση. Οι προπληρωμές (DataForSEO, DeepSeek) ΔΕΝ είναι κόστος — μετράει η κατανάλωση.',
        month: 'Μήνας', revenue: 'Έσοδα', cost: 'Κόστος', breakdown: 'Ανάλυση κόστους', margin: 'Περιθώριο',
        current: 'τρέχων', total: 'Σύνολο', lines: '{n} γραμμές · {inv} τιμολογημένες',
        prepaid: 'Προπληρωμή (όχι κόστος)',
        src: { invoice: 'από τιμολόγιο', metered: 'μετρημένη κατανάλωση', estimate: 'εκτίμηση από τη χρήση', carried: 'δεν έχει τιμολογηθεί ακόμη — ποσό προηγούμενου μήνα' },
        srcShort: { invoice: 'Τ', metered: 'Μ', estimate: 'Ε', carried: '≈' },
        legend: 'Τ = τιμολόγιο · Μ = μετρημένη κατανάλωση · Ε = εκτίμηση · ≈ δεν έχει τιμολογηθεί ακόμη (ποσό προηγούμενου μήνα).',
        accountsHeading: '👥 Ανά πελάτη — έσοδα και μεταβλητό κόστος των Brains του (από Ιανουάριο)',
        accountsIntro: 'Μεταβλητό κόστος = μοντέλα AI + embeddings + DataForSEO των Brains του λογαριασμού. Η υποδομή (server, Chroma) είναι κοινή και δεν επιμερίζεται.',
        customer: 'Πελάτης', brains: 'Brains', variableCost: 'Μεταβλητό κόστος',
      },
      reconciliation: {
        heading: '🧾 Εκτίμηση vs Πραγματικότητα — ανά μήνα/provider',
        estimated: 'Εκτίμηση (διάστημα)',
        actual: 'Πραγματικό (από invoices)',
        variance: 'Διαφορά',
        table: {
          month: 'Μήνας',
          provider: 'Provider',
          estimated: 'Εκτίμηση',
          actual: 'Πραγματικό',
          variance: 'Διαφορά',
          pct: '%',
        },
      },
      invoices: {
        heading: '📋 Provider invoices (χειροκίνητη καταχώριση)',
        amountPlaceholder: 'EUR',
        invoiceRefPlaceholder: 'Invoice #',
        notesPlaceholder: 'Notes (προαιρετικό)',
        submit: 'Καταχώριση',
        empty: 'Δεν έχουν καταχωρηθεί τιμολόγια ακόμα.',
        confirmDelete: 'Διαγραφή; Δεν αναιρείται.',
        table: {
          month: 'Μήνας',
          provider: 'Provider',
          amount: 'Ποσό (EUR)',
          original: 'Original',
          ref: 'Invoice #',
          notes: 'Notes',
        },
      },
      perModel: {
        heading: 'Ανά μοντέλο',
        provider: 'Provider',
        model: 'Model',
        bots: 'Bots',
        messages: 'Messages',
        inputTokens: 'Input tokens',
        outputTokens: 'Output tokens',
        cost: 'Κόστος',
        pctTotal: '% του συνόλου',
      },
      perProvider: {
        heading: 'Ανά provider',
        provider: 'Provider',
        messages: 'Messages',
        inputTokens: 'Input tokens',
        outputTokens: 'Output tokens',
        cost: 'Κόστος',
        pctTotal: '% του συνόλου',
      },
      topBots: {
        heading: 'Top {n} bots κατά κόστος',
        full: 'Πλήρης λίστα bots ({n})',
        bot: 'Bot',
        team: 'Team',
        plan: 'Plan',
        messages: 'Messages',
        input: 'Input',
        output: 'Output',
        cost: 'Κόστος',
      },
      pricing: {
        heading: '📋 Pricing catalog ({n} models)',
        provider: 'Provider',
        model: 'Model',
        inputPer: 'Input €/1M',
        outputPer: 'Output €/1M',
        cacheHit: 'Cache hit',
        effectiveFrom: 'Effective from',
        notes: 'Notes',
        footnote: 'Όλες οι τιμές σε micro-EUR (1e-6 EUR). USD→EUR conversion στις 0.93 (snapshot 2026-04-28). Για επικαιροποίηση, ενημέρωση direct στο model_pricing table.',
      },
    },
    contactRequests: {
      title: 'Φόρμες επικοινωνίας',
      heading: 'Contact requests',
      subtitle: '{n} αιτήματα από τη φόρμα του vistochat.com — admin only.',
      filterAll: 'Όλα',
      searchPlaceholder: 'Email, όνομα, εταιρία, τηλέφωνο, μήνυμα…',
      empty: {
        heading: 'Κανένα contact request',
        body: 'Όταν επισκέπτες συμπληρώνουν τη φόρμα στο vistochat.com, εμφανίζονται εδώ.',
      },
      table: {
        details: 'Στοιχεία',
        message: 'Μήνυμα',
        source: 'Πηγή',
        captured: 'Καταγραφή',
        status: 'Status',
        followup: 'Followup',
      },
      pager: {
        prev: '‹ Προηγ.',
        next: 'Επόμ. ›',
        info: 'Σελίδα {page} / {total} · {n} σύνολο',
      },
      detail: {
        ariaLabel: 'Αίτημα επικοινωνίας',
      },
      drawer: {
        contactInfo: 'Στοιχεία επικοινωνίας',
        labelName: 'Όνομα',
        labelEmail: 'Email',
        labelPhone: 'Τηλέφωνο',
        labelCompany: 'Εταιρία',
        labelWebsite: 'Website',
        labelIp: 'IP',
        labelSource: 'Πηγή',
        labelSourceUrl: 'Source URL',
        labelNotify: 'Notify',
        notifySent: '✓ {date}',
        notifyNotSent: '— (δεν στάλθηκε)',
        message: 'Μήνυμα',
        statusNotes: 'Status & Σημειώσεις',
        statusLabel: 'Status',
        statusNote: 'Σημείωση status (προαιρετικό)',
        internalNotes: 'Εσωτερικές σημειώσεις',
        followupDate: 'Followup ημ/νία',
        delete: 'Διαγραφή',
        save: 'Αποθήκευση αλλαγών',
        saving: 'Αποθήκευση…',
        closeAria: 'Κλείσιμο',
        confirmDelete: 'Διαγραφή του request από "{label}"; Αυτή η ενέργεια δεν αναιρείται.',
        unknown: 'άγνωστο',
      },
      errors: {
        accessDenied: 'Μόνο admin.',
        loadError: 'Σφάλμα φόρτωσης',
        saveError: 'Σφάλμα αποθήκευσης',
        deleteError: 'Σφάλμα διαγραφής',
      },
      status: {
        new: 'Νέο',
        contacted: 'Επικοινωνία',
        qualified: 'Qualified',
        closed: 'Κλείστηκε',
        lost: 'Χάθηκε',
        spam: 'Spam',
      },
    },
  },
  verticals: {
    fieldLabel: 'Είδος επιχείρησης',
    column: 'Κατηγορία',
    fieldHint: 'Ορίζει ποια skills εμφανίζονται πρώτα στον κατάλογο. Δεν ενεργοποιεί ούτε απενεργοποιεί τίποτα.',
    hiddenNote: '{n} skills δεν αφορούν συνήθως «{kind}» και είναι κρυμμένα.',
    showAll: 'Εμφάνιση όλων',
    showFitting: 'Μόνο όσα ταιριάζουν',
    general: { label: 'Γενικό (όλα τα skills)', short: 'Γενικό' },
    hotel: { label: 'Ξενοδοχείο / κατάλυμα', short: 'Ξενοδοχείο' },
    store: { label: 'Κατάστημα / e-shop', short: 'Κατάστημα' },
    services: { label: 'Υπηρεσίες / επαγγελματίες', short: 'Υπηρεσίες' },
    betting: { label: 'Στοίχημα / iGaming', short: 'Στοίχημα' },
  },
  plugins: {
    title: 'Plugins',
    subtitle: 'Όλα τα Brains και τα κανάλια/skills τους σε έναν πίνακα: τι τρέχει πού, τι επιτρέπει ακόμη το πακέτο και τι χρειάζεται προσοχή. Πατήστε ένα κελί για να το ρυθμίσετε.',
    loading: 'Φόρτωση…',
    loadError: 'Δεν ήταν δυνατή η φόρτωση.',
    search: 'Αναζήτηση Brain…',
    allCategories: 'Όλες οι κατηγορίες',
    company: 'Εταιρεία',
    allCompanies: 'Όλες οι εταιρείες',
    allColumns: 'Όλες οι στήλες',
    count: '{n} Brains',
    empty: 'Κανένα Brain δεν ταιριάζει στα φίλτρα.',
    colBrain: 'Brain',
    colCustom: 'Custom',
    attentionTitle: 'Χρειάζονται προσοχή ({n})',
    state: {
      active: 'Ενεργό',
      issue: 'Ενεργό — χρειάζεται προσοχή',
      overLimit: 'Ενεργό αλλά εκτός πακέτου — ΔΕΝ λειτουργεί',
      available: 'Διαθέσιμο στο πακέτο — ανενεργό',
      upgrade: 'Χρειάζεται μεγαλύτερο πακέτο',
      requires: 'Χρειάζεται πρώτα άλλο plugin',
      none: 'Δεν αφορά αυτή την κατηγορία',
    },
    issues: {
      over_limit: 'Ενεργό αλλά πάνω από τις θέσεις του πακέτου — δεν λειτουργεί.',
      reviews_stale: 'Οι κριτικές δεν έχουν ανανεωθεί εδώ και {days} ημέρες.',
      reviews_error: 'Η τελευταία ανανέωση κριτικών απέτυχε.',
      prices_stale: 'Η μέτρηση τιμών δεν έχει ανανεωθεί εδώ και {days} ημέρες.',
      prices_claim_hidden: 'Η μέτρηση τιμών είναι παλιά ({days} ημέρες) — το «συνήθως N% φθηνότερα» δεν εμφανίζεται.',
      prices_error: 'Η τελευταία μέτρηση τιμών απέτυχε.',
      booking_no_url: 'Έχει επιλεγεί μηχανή κρατήσεων χωρίς σύνδεσμο.',
      catalog_no_url: 'Έχει επιλεγεί κατάλογος προϊόντων χωρίς διεύθυνση σύνδεσης.',
      email_error: 'Ο τελευταίος έλεγχος γραμματοκιβωτίου (IMAP/SMTP) απέτυχε.',
    },
  },
};
